import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import HealthCheck from './health_check';
import { cachedApiCall, prefetchCriticalData } from './cache-manager';
import SimpleLawyerDashboard from './SimpleLawyerDashboard';
import PostHogAnalytics from './PostHogAnalytics';
import PwaInstallBanner from './PwaInstallBanner';

// Homepage component moved outside App to prevent re-creation on re-renders
const HomePage = ({ setCurrentView, backendUrl }) => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  
  // Debug function to ensure navigation works
  const handleNavigation = (view) => {
    console.log('Navigating to:', view);
    setCurrentView(view);
  };
  
  const testimonials = [
    {
      name: "Lupita M.",
      location: "Ecatepec, Estado de México",
      case: "Pensión alimenticia",
      text: "La verdad tenía mis dudas pero me arriesgué. A las pocas horas me escribieron 3 abogados y uno me cayó muy bien. Mi caso de pensión tardó como 4 meses pero al final mi ex ya está pagando.",
      rating: 4,
      result: "Pensión asegurada",
      amount: "$4,800 MXN mensuales"
    },
    {
      name: "Roberto Sánchez",
      location: "Iztapalapa, CDMX",
      case: "Despido injustificado",
      text: "Me corrieron de la fábrica sin liquidación después de 6 años. No sabía ni por dónde empezar. El abogado que me contactó fue muy derecho conmigo y recuperamos casi todo lo que me debían.",
      rating: 5,
      result: "Liquidación recuperada",
      amount: "$67,000 MXN"
    },
    {
      name: "Carmen de León",
      location: "Guadalajara, Jalisco",
      case: "Divorcio",
      text: "Necesitaba divorciarme rápido porque mi situación era difícil. Me llegaron 4 propuestas y escogí a una abogada que me explicó todo clarito. En 3 meses ya estaba divorciada.",
      rating: 5,
      result: "Divorcio express",
      amount: "Sin complicaciones"
    },
    {
      name: "Jorge Hdz.",
      location: "Tlalnepantla, Edo. Mex",
      case: "Accidente de trabajo",
      text: "Me lastimé la espalda en la obra y el patrón no quería responder. Un abogado de aquí me ayudó a meter la demanda al IMSS. Tardó pero sí salió la incapacidad.",
      rating: 4,
      result: "Incapacidad permanente",
      amount: "$38,500 MXN + pensión"
    },
    {
      name: "Mariana Torres",
      location: "Monterrey, Nuevo León",
      case: "Deuda bancaria",
      text: "Debía como $120 mil al banco y ya no podía ni dormir. El abogado negoció y terminé pagando menos de la mitad. No es magia pero sí me sacó del hoyo.",
      rating: 4,
      result: "Deuda negociada",
      amount: "Pagué solo $52,000"
    },
    {
      name: "Don Beto",
      location: "Naucalpan, Edo. Mex",
      case: "Herencia",
      text: "Mis hermanos no querían repartir la casa de mi mamá. Llevábamos 2 años peleando. Con el abogado que encontré aquí por fin se hizo el juicio y ya cada quien tiene su parte.",
      rating: 5,
      result: "Herencia repartida",
      amount: "Terreno escriturado"
    },
    {
      name: "Adriana Vega",
      location: "Puebla, Puebla",
      case: "Custodia de hijos",
      text: "Mi ex se quería llevar a mis hijos a otro estado. Estaba desesperada. La abogada que me tocó fue buenísima y ganamos la custodia. Le estaré agradecida siempre.",
      rating: 5,
      result: "Custodia ganada",
      amount: "Mis hijos conmigo"
    },
    {
      name: "Paco Ramírez",
      location: "León, Guanajuato",
      case: "Fraude",
      text: "Me estafaron con un carro que salió robado. Pensé que había perdido mi dinero. El abogado metió la denuncia bien hecha y recuperamos una parte. No todo pero algo es algo.",
      rating: 4,
      result: "Denuncia procedente",
      amount: "$28,000 MXN recuperados"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-blue-50 to-purple-50">
      {/* Professional Header */}
      <nav className="bg-white shadow-lg border-b-4 border-blue-600 relative z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            
            {/* Professional Logo - Left Side */}
            <div className="flex items-center space-x-3">
              {/* Clean Legal Icon */}
              <div className="flex-shrink-0">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center shadow-md">
                  <svg className="w-6 h-6 lg:w-7 lg:h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2M8 20V18H16V20C16 21.1 15.1 22 14 22H10C8.9 22 8 21.1 8 20Z"/>
                  </svg>
                </div>
              </div>
              
              {/* Professional Typography */}
              <div className="hidden sm:flex flex-col">
                <div className="flex items-center text-xl lg:text-2xl font-bold text-gray-900 leading-tight">
                  <span>AYUDA<span className="text-blue-600">LEGAL</span></span>
                  <svg className="ml-2 w-6 h-4 lg:w-7 lg:h-5" viewBox="0 0 21 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="21" height="14" fill="#006847"/>
                    <rect width="21" height="9.33" fill="#FFFFFF"/>
                    <rect width="21" height="4.67" fill="#CE1126"/>
                    <g transform="translate(10.5, 7)">
                      <circle cx="0" cy="0" r="1.5" fill="#8B4513"/>
                      <rect x="-0.3" y="-1" width="0.6" height="2" fill="#228B22"/>
                    </g>
                  </svg>
                </div>
                <div className="text-xs lg:text-sm text-blue-600 font-semibold tracking-wide">
                  POWERED BY IA
                </div>
              </div>
              
              {/* Mobile Logo */}
              <div className="sm:hidden">
                <div className="flex items-center text-lg font-bold text-gray-900">
                  <span>AYUDA<span className="text-blue-600">LEGAL</span></span>
                  <svg className="ml-2 w-5 h-3" viewBox="0 0 21 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="21" height="14" fill="#006847"/>
                    <rect width="21" height="9.33" fill="#FFFFFF"/>
                    <rect width="21" height="4.67" fill="#CE1126"/>
                    <g transform="translate(10.5, 7)">
                      <circle cx="0" cy="0" r="1.5" fill="#8B4513"/>
                      <rect x="-0.3" y="-1" width="0.6" height="2" fill="#228B22"/>
                    </g>
                  </svg>
                </div>
              </div>
            </div>

            {/* Navigation - Right Side */}
            <div className="flex items-center space-x-2 lg:space-x-4">
              
              {/* Admin Portal - Desktop */}
              <button 
                onClick={() => setCurrentView('admin-login')}
                className="hidden lg:block text-gray-600 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors duration-200"
              >
                Admin Portal
              </button>
              
              {/* Partner Dashboard - Desktop */}
              <button 
                onClick={() => setCurrentView('partner-login')}
                className="hidden lg:block text-gray-600 hover:text-green-600 px-3 py-2 text-sm font-medium transition-colors duration-200"
              >
                📊 Dashboard Socio
              </button>

              {/* Portal Abogados - Mobile Optimized */}
              <button 
                onClick={() => setCurrentView('lawyer-login')}
                className="bg-blue-600 text-white px-3 py-2 lg:px-4 lg:py-2 rounded-lg font-semibold text-sm lg:text-base hover:bg-blue-700 transition-colors duration-200 shadow-md"
              >
                <span className="hidden sm:inline">Portal Abogados</span>
                <span className="sm:hidden">Portal</span>
              </button>

              {/* Main CTA - Mobile Optimized */}
              <button 
                onClick={() => handleNavigation('case-form')}
                className="bg-gradient-to-r from-green-600 to-green-700 text-white px-3 py-2 lg:px-6 lg:py-2 rounded-lg font-bold text-sm lg:text-base hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-md"
              >
                <span className="hidden sm:inline">Consulta Gratis</span>
                <span className="sm:hidden">Consulta</span>
              </button>
              
              {/* Hamburger Menu for Admin on Mobile */}
              <button 
                onClick={() => setCurrentView('admin-login')}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:text-blue-600 hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"/>
                </svg>
              </button>
              
              {/* Partner Dashboard on Mobile */}
              <button 
                onClick={() => setCurrentView('partner-login')}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:text-green-600 hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Professional Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-purple-900/20"></div>

        <div className="relative max-w-7xl mx-auto px-4 py-12 lg:py-20 text-center">
          {/* Professional Logo Display */}
          <div className="flex justify-center items-center mb-8 lg:mb-12">
            <div className="text-center max-w-2xl mx-auto">
              
              {/* Large Professional Logo */}
              <div className="mb-6">
                <div className="w-24 h-24 lg:w-32 lg:h-32 mx-auto bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-2xl mb-6 border border-white/30">
                  <svg className="w-12 h-12 lg:w-16 lg:h-16 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2M8 20V18H16V20C16 21.1 15.1 22 14 22H10C8.9 22 8 21.1 8 20Z"/>
                  </svg>
                </div>
                
                <h1 className="text-4xl lg:text-6xl xl:text-7xl font-bold text-white mb-3">
                  AYUDA<span className="text-yellow-300">LEGAL</span>
                </h1>
                <div className="text-xl lg:text-2xl text-yellow-300 font-semibold tracking-wider mb-4">
                  POWERED BY INTELIGENCIA ARTIFICIAL
                </div>
                <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-green-400 mx-auto rounded-full"></div>
              </div>

              <div className="space-y-3">
                <div className="inline-flex items-center bg-yellow-400 text-blue-900 px-4 py-2 rounded-lg text-sm sm:text-base font-bold shadow-lg">
                  <span>🧠 POWERED BY TIKs</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mb-8 px-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-white leading-tight">
              Conectamos Clientes con Abogados Especializados
            </h2>
            <p className="text-lg sm:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
              Tecnología de inteligencia artificial para encontrar el abogado perfecto para tu caso
            </p>
          </div>

          {/* IA Technology Section */}
          <div className="px-4 mb-8">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-6 max-w-4xl mx-auto text-center border border-white/30">
              <p className="text-base sm:text-lg text-white leading-relaxed">
                <strong className="text-yellow-300">INTELIGENCIA ARTIFICIAL:</strong> Análisis automático en 6 capas + Matching inteligente con 3 abogados especializados
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-4 text-sm text-white">
                <span>✅ Exitosa en México</span>
                <span>✅ Primeros casos en México</span>
                <span>✅ Verificación por IA</span>
              </div>
            </div>
          </div>
          
          {/* IA Technology Badges */}
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 mb-8 max-w-3xl mx-auto border border-white/30">
            <div className="grid md:grid-cols-3 gap-4 text-white text-sm">
              <div className="flex items-center justify-center">
                <span className="mr-2">🧠</span>
                <span><strong>IA de Verificación:</strong> Detecta casos falsos</span>
              </div>
              <div className="flex items-center justify-center">
                <span className="mr-2">🎯</span>
                <span><strong>Matching Inteligente:</strong> Encuentra especialistas</span>
              </div>
              <div className="flex items-center justify-center">
                <span className="mr-2">⚡</span>
                <span><strong>Procesamiento IA:</strong> Resultados en 24h</span>
              </div>
            </div>
          </div>

          {/* Key Value Props - Clear benefits */}
          <div className="grid md:grid-cols-3 gap-8 mb-12 max-w-4xl mx-auto">
            <div className="bg-green-500/30 backdrop-blur-sm rounded-xl p-6 border border-green-400/50">
              <div className="text-3xl font-bold text-white mb-2">100% GRATIS</div>
              <div className="text-green-200">Para encontrar tu abogado</div>
            </div>
            <div className="bg-blue-500/30 backdrop-blur-sm rounded-xl p-6 border border-blue-400/50">
              <div className="text-3xl font-bold text-white mb-2">⚡ 24h</div>
              <div className="text-blue-200">Respuesta IA garantizada</div>
            </div>
            <div className="bg-purple-500/30 backdrop-blur-sm rounded-xl p-6 border border-purple-400/50">
              <div className="text-3xl font-bold text-white mb-2">🧠 6 Capas</div>
              <div className="text-purple-200">Verificación por IA</div>
            </div>
          </div>

          {/* Primary CTA - Clear and simple */}
          <div className="space-y-4">
            <button 
              onClick={() => handleNavigation('case-form')}
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-4 sm:px-12 sm:py-5 rounded-full text-lg sm:text-2xl font-bold hover:shadow-2xl transition-all duration-300 hover:scale-105 transform pulse-animation shadow-2xl w-full sm:w-auto"
            >
              <span className="sm:hidden">🤖 ACTIVAR IA</span>
              <span className="hidden sm:inline">🤖 ACTIVAR IA - ENCONTRAR MI ABOGADO</span>
            </button>
            
            <div className="text-xs sm:text-sm text-white space-y-1 px-4 sm:px-0">
              <div>✅ IA analiza tu caso automáticamente</div>
              <div>✅ Matching inteligente con abogados especializados</div>
              <div>✅ Tu eliges con quien trabajar y el precio</div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="bg-gray-50 py-16 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h3 className="text-3xl font-bold text-gray-900 mb-12">Resultados que Hablan por Sí Solos</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-blue-200 shadow-lg">
              <div className="text-2xl sm:text-4xl font-bold text-blue-600 mb-1 sm:mb-2">+2,847</div>
              <div className="text-gray-800 font-medium text-sm sm:text-base">Casos Procesados</div>
              <div className="text-xs sm:text-sm text-gray-600 mt-1">Por IA en 2024</div>
            </div>
            
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-green-200 shadow-lg">
              <div className="text-2xl sm:text-4xl font-bold text-green-600 mb-1 sm:mb-2">94%</div>
              <div className="text-gray-800 font-medium text-sm sm:text-base">Casos Exitosos</div>
              <div className="text-xs sm:text-sm text-gray-600 mt-1">Resolución favorable</div>
            </div>
            
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-purple-200 shadow-lg">
              <div className="text-2xl sm:text-4xl font-bold text-purple-600 mb-1 sm:mb-2">387</div>
              <div className="text-gray-800 font-medium text-sm sm:text-base">Abogados Activos</div>
              <div className="text-xs sm:text-sm text-gray-600 mt-1">Verificados por IA</div>
            </div>
            
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-yellow-200 shadow-lg">
              <div className="text-2xl sm:text-4xl font-bold text-yellow-600 mb-1 sm:mb-2">18h</div>
              <div className="text-gray-800 font-medium text-sm sm:text-base">Tiempo Promedio</div>
              <div className="text-xs sm:text-sm text-gray-600 mt-1">Para recibir propuestas</div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="bg-blue-600 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">
              Lo que Dicen Nuestros Clientes
            </h3>
            <p className="text-lg text-blue-200">
              Casos reales resueltos con nuestra inteligencia artificial
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
            {testimonials[currentTestimonial] && (
              <div className="text-center">
                <div className="flex justify-center mb-6">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                
                <blockquote className="text-xl text-gray-800 leading-relaxed mb-6 italic font-medium">
                  "{testimonials[currentTestimonial].text}"
                </blockquote>
                
                <div className="border-t border-gray-200 pt-6">
                  <div className="font-bold text-lg text-gray-900">
                    {testimonials[currentTestimonial].name}
                  </div>
                  <div className="text-blue-600 font-medium">
                    {testimonials[currentTestimonial].location} • {testimonials[currentTestimonial].case}
                  </div>
                  <div className="mt-2 flex justify-center space-x-6 text-sm">
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">
                      ✅ {testimonials[currentTestimonial].result}
                    </div>
                    <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">
                      💰 {testimonials[currentTestimonial].amount}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-center mt-6 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentTestimonial ? 'bg-white' : 'bg-blue-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              ¿Cómo Funciona Nuestra IA?
            </h3>
            <p className="text-lg text-gray-700">
              Proceso automatizado para conectarte con el abogado perfecto
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center bg-gray-50 rounded-xl p-8 border border-gray-200">
              <div className="bg-blue-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">
                📝 Describes tu Caso
              </h4>
              <p className="text-gray-700">
                Nuestra IA analiza tu situación legal en 6 capas de verificación para asegurar que sea un caso válido y bien documentado.
              </p>
            </div>
            
            <div className="text-center bg-gray-50 rounded-xl p-8 border border-gray-200">
              <div className="bg-green-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">
                🧠 IA Busca Especialistas
              </h4>
              <p className="text-gray-700">
                Sistema inteligente encuentra los 3 abogados más compatibles según tu área legal, ubicación y tipo de caso específico.
              </p>
            </div>
            
            <div className="text-center bg-gray-50 rounded-xl p-8 border border-gray-200">
              <div className="bg-purple-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">
                🤝 Recibes Propuestas
              </h4>
              <p className="text-gray-700">
                Los abogados especializados te contactan con propuestas personalizadas. Tú decides con quién trabajar y a qué precio.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Areas Section */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Áreas Legales Especializadas
            </h3>
            <p className="text-lg text-gray-700">
              Conectamos con abogados expertos en cada área del derecho
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { name: 'Derecho de Familia', icon: '👨‍👩‍👧‍👦', description: 'Divorcio, custodia, pensiones' },
              { name: 'Derecho Laboral', icon: '💼', description: 'Despidos, indemnizaciones' },
              { name: 'Derecho Civil', icon: '⚖️', description: 'Contratos, responsabilidad civil' },
              { name: 'Derecho Penal', icon: '🏛️', description: 'Defensa criminal' },
              { name: 'Derecho Comercial', icon: '🏢', description: 'Empresas, sociedades' },
              { name: 'Derecho Inmobiliario', icon: '🏠', description: 'Compraventa, arriendos' },
              { name: 'Derecho Tributario', icon: '📊', description: 'Impuestos, SII' },
              { name: 'Herencias', icon: '📜', description: 'Sucesiones, testamentos' }
            ].map((area, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-gray-300 hover:shadow-lg transition-shadow hover:border-blue-300">
                <div className="text-4xl mb-4 text-center">{area.icon}</div>
                <h4 className="font-bold text-gray-900 text-center mb-2">{area.name}</h4>
                <p className="text-gray-700 text-sm text-center">{area.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 py-16">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h3 className="text-4xl font-bold text-white mb-6">
            🤖 Activa la IA Ahora
          </h3>
          <p className="text-xl text-blue-100 mb-8">
            Miles de casos resueltos • Abogados verificados • Respuesta en 24h
          </p>
          
          <div className="space-y-4">
            <button 
              onClick={() => handleNavigation('case-form')}
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-4 sm:px-12 sm:py-6 rounded-full text-lg sm:text-2xl font-bold shadow-2xl hover:scale-105 transform transition-all duration-300 w-full sm:w-auto"
            >
              <span className="sm:hidden">🚀 ENCONTRAR ABOGADO</span>
              <span className="hidden sm:inline">🚀 ENCONTRAR MI ABOGADO AHORA</span>
            </button>
            
            <div className="text-blue-100 space-y-2">
              <div className="flex items-center justify-center space-x-8 text-sm">
                <span>✅ 100% Gratis para encontrar abogado</span>
                <span>✅ IA analiza tu caso en profundidad</span>
                <span>✅ Solo abogados especializados</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section for Lawyers */}
      <div className="bg-gray-800 py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">
            ¿Eres Abogado? 
          </h3>
          <p className="text-xl text-gray-300 mb-8">
            Únete a nuestra red de profesionales y recibe casos verificados por IA
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-700 rounded-xl p-6 border border-gray-600">
              <div className="text-3xl font-bold text-green-400 mb-2">PRIMER CASO</div>
              <div className="text-lg font-semibold text-white mb-2">100% GRATIS</div>
              <div className="text-gray-300 text-sm">Sin costo de inscripción inicial</div>
            </div>
            
            <div className="bg-gray-700 rounded-xl p-6 border border-gray-600">
              <div className="text-3xl font-bold text-blue-400 mb-2">CASOS IA</div>
              <div className="text-lg font-semibold text-white mb-2">PRE-VERIFICADOS</div>
              <div className="text-gray-300 text-sm">Solo clientes reales y casos válidos</div>
            </div>
            
            <div className="bg-gray-700 rounded-xl p-6 border border-gray-600">
              <div className="text-3xl font-bold text-purple-400 mb-2">MATCHING</div>
              <div className="text-lg font-semibold text-white mb-2">INTELIGENTE</div>
              <div className="text-gray-300 text-sm">Solo casos de tu especialidad</div>
            </div>
          </div>
          
          <div className="space-y-4">
            <button 
              onClick={() => setCurrentView('lawyer-register')}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 text-gray-900 px-6 py-3 sm:px-12 sm:py-4 rounded-full text-lg sm:text-xl font-bold hover:shadow-2xl transition-all duration-300 hover:scale-105 w-full sm:w-auto"
            >
              <span className="sm:hidden">⚖️ INSCRIBIRSE GRATIS</span>
              <span className="hidden sm:inline">⚖️ INSCRIBIRSE GRATIS - PRIMER CASO GRATUITO</span>
            </button>
            
            <div className="text-gray-300 text-sm">
              <span>Ya tienes cuenta? </span>
              <button 
                onClick={() => setCurrentView('lawyer-login')}
                className="text-blue-400 hover:text-blue-300 font-semibold underline"
              >
                Inicia Sesión Aquí
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 🆕 BLOQUE PROMINENTE PARA ABOGADOS - TU IDEA IMPLEMENTADA */}
      <div className="bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-3xl p-8 sm:p-12 border border-white border-opacity-20 shadow-2xl">
            
            {/* Título Principal */}
            <div className="text-5xl mb-6">👨‍⚖️</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
              ¿Eres abogado especializado?
            </h2>
            
            {/* Propuesta de Valor */}
            <p className="text-xl sm:text-2xl text-blue-100 font-medium mb-8 leading-relaxed">
              Recibe casos verificados por IA. Sin inscripción ni mensualidades.
            </p>
            
            {/* CTAs Duales */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <button
                onClick={() => setCurrentView('lawyer-login')}
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-xl text-lg font-bold hover:from-green-600 hover:to-emerald-700 transition-all duration-300 transform hover:scale-105 shadow-xl"
              >
                🔑 ACCEDER AL PORTAL
              </button>
              <button
                onClick={() => setCurrentView('lawyer-register')}
                className="bg-gradient-to-r from-yellow-500 to-orange-500 text-gray-900 px-8 py-4 rounded-xl text-lg font-bold hover:from-yellow-600 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-xl"
              >
                🎁 REGISTRARSE GRATIS
              </button>
            </div>
            
            {/* Microcopy de Valor */}
            <p className="text-blue-200 text-base italic">
              — Solo clientes reales y precalificados por IA —
            </p>
            
            {/* Beneficios Destacados */}
            <div className="grid sm:grid-cols-3 gap-4 mt-8 text-sm">
              <div className="bg-white bg-opacity-10 rounded-lg p-3">
                <div className="text-2xl mb-2">🧠</div>
                <div className="text-white font-medium">IA en 6 pasos</div>
                <div className="text-blue-200 text-xs">Casos pre-analizados</div>
              </div>
              <div className="bg-white bg-opacity-10 rounded-lg p-3">
                <div className="text-2xl mb-2">🎯</div>
                <div className="text-white font-medium">Solo tu especialidad</div>
                <div className="text-blue-200 text-xs">Matching inteligente</div>
              </div>
              <div className="bg-white bg-opacity-10 rounded-lg p-3">
                <div className="text-2xl mb-2">💰</div>
                <div className="text-white font-medium">Primer caso GRATIS</div>
                <div className="text-blue-200 text-xs">Solo pagas lo que usas</div>
              </div>
            </div>
            
          </div>
        </div>
      </div>

      {/* Footer */}
      <PwaInstallBanner />

      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2M8 20V18H16V20C16 21.1 15.1 22 14 22H10C8.9 22 8 21.1 8 20Z"/>
                  </svg>
                </div>
                <div>
                  <div className="text-xl font-bold">AYUDA<span className="text-blue-400">LEGAL</span></div>
                  <div className="text-sm text-blue-400">POWERED BY IA</div>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Plataforma de inteligencia artificial para conectar clientes con abogados especializados.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Para Clientes</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>• Envía tu caso gratis</li>
                <li>• Recibe propuestas en 24h</li>
                <li>• Elige tu abogado ideal</li>
                <li>• Verificación por IA</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Para Abogados</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>• Casos verificados por IA</li>
                <li>• Clientes pre-calificados</li>
                <li>• Primer caso gratuito</li>
                <li>• Registro profesional</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2024 AyudaLegal IA. Todos los derechos reservados.</p>
            <p className="mt-2">🧠 Powered by Inteligencia Artificial • ⚖️ Verificación Legal Profesional</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ForgotPassword component
const ForgotPassword = ({ backendUrl, setCurrentView, forgotPasswordData, setForgotPasswordData }) => {
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!forgotPasswordData.email.trim()) {
      setForgotPasswordData(prev => ({ ...prev, message: 'Por favor ingresa tu email' }));
      return;
    }
    
    setForgotPasswordData(prev => ({ ...prev, isLoading: true, message: '' }));
    
    try {
      const response = await fetch(`${backendUrl}/api/lawyers/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordData.email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setForgotPasswordData(prev => ({ 
          ...prev, 
          message: data.message,
          isLoading: false 
        }));
      } else {
        setForgotPasswordData(prev => ({ 
          ...prev, 
          message: data.detail || 'Error al enviar el email de recuperación',
          isLoading: false 
        }));
      }
    } catch (error) {
      setForgotPasswordData(prev => ({ 
        ...prev, 
        message: 'Error de conexión. Intenta nuevamente.',
        isLoading: false 
      }));
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-2xl border border-gray-200">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900">🔑 Recuperar Contraseña</h2>
          <p className="text-gray-600 mt-2">Te enviaremos un enlace para restablecer tu contraseña</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Correo Electrónico
            </label>
            <input
              id="email"
              type="email"
              value={forgotPasswordData.email}
              onChange={(e) => setForgotPasswordData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="tu@email.com"
              disabled={forgotPasswordData.isLoading}
              required
            />
          </div>

          {forgotPasswordData.message && (
            <div className={`p-4 rounded-lg text-sm ${
              forgotPasswordData.message.includes('recibirás') || forgotPasswordData.message.includes('enviado')
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {forgotPasswordData.message}
            </div>
          )}

          <button
            type="submit"
            disabled={forgotPasswordData.isLoading}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 disabled:opacity-50"
          >
            {forgotPasswordData.isLoading ? 'Enviando...' : '📧 Enviar Enlace de Recuperación'}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <button 
            onClick={() => setCurrentView('lawyer-login')}
            className="text-blue-600 hover:text-blue-800 underline text-sm"
          >
            ← Volver al login
          </button>
        </div>
      </div>
    </div>
  );
};

// ResetPassword component
const ResetPassword = ({ backendUrl, setCurrentView, resetPasswordData, setResetPasswordData }) => {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  
  // Set token on component mount
  React.useEffect(() => {
    if (token && !resetPasswordData.token) {
      setResetPasswordData(prev => ({ ...prev, token }));
    }
  }, [token, resetPasswordData.token, setResetPasswordData]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!resetPasswordData.password || !resetPasswordData.confirmPassword) {
      setResetPasswordData(prev => ({ 
        ...prev, 
        message: 'Por favor completa todos los campos' 
      }));
      return;
    }
    
    if (resetPasswordData.password !== resetPasswordData.confirmPassword) {
      setResetPasswordData(prev => ({ 
        ...prev, 
        message: 'Las contraseñas no coinciden' 
      }));
      return;
    }
    
    if (resetPasswordData.password.length < 6) {
      setResetPasswordData(prev => ({ 
        ...prev, 
        message: 'La contraseña debe tener al menos 6 caracteres' 
      }));
      return;
    }
    
    setResetPasswordData(prev => ({ ...prev, isLoading: true, message: '' }));
    
    try {
      const response = await fetch(`${backendUrl}/api/lawyers/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          token: resetPasswordData.token, 
          new_password: resetPasswordData.password 
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setResetPasswordData(prev => ({ 
          ...prev, 
          message: 'success:' + data.message,
          isLoading: false 
        }));
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          setCurrentView('lawyer-login');
        }, 3000);
      } else {
        setResetPasswordData(prev => ({ 
          ...prev, 
          message: data.detail || 'Error al restablecer la contraseña',
          isLoading: false 
        }));
      }
    } catch (error) {
      setResetPasswordData(prev => ({ 
        ...prev, 
        message: 'Error de conexión. Intenta nuevamente.',
        isLoading: false 
      }));
    }
  };
  
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-2xl border border-gray-200 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">❌ Enlace Inválido</h2>
          <p className="text-gray-600 mb-6">El enlace de recuperación no es válido o ha expirado.</p>
          <button 
            onClick={() => setCurrentView('forgot-password')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Solicitar Nuevo Enlace
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-2xl border border-gray-200">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900">🔒 Nueva Contraseña</h2>
          <p className="text-gray-600 mt-2">Ingresa tu nueva contraseña</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Nueva Contraseña
            </label>
            <input
              id="password"
              type="password"
              value={resetPasswordData.password}
              onChange={(e) => setResetPasswordData(prev => ({ ...prev, password: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Mínimo 6 caracteres"
              disabled={resetPasswordData.isLoading}
              required
            />
          </div>
          
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Confirmar Contraseña
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={resetPasswordData.confirmPassword}
              onChange={(e) => setResetPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Repite la contraseña"
              disabled={resetPasswordData.isLoading}
              required
            />
          </div>

          {resetPasswordData.message && (
            <div className={`p-4 rounded-lg text-sm ${
              resetPasswordData.message.includes('success:')
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {resetPasswordData.message.replace('success:', '')}
              {resetPasswordData.message.includes('success:') && (
                <p className="mt-2 text-xs">
                  Redirigiendo al login en 3 segundos...
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={resetPasswordData.isLoading}
            className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 px-4 rounded-lg font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-300 transform hover:scale-105 disabled:opacity-50"
          >
            {resetPasswordData.isLoading ? 'Actualizando...' : '✅ Actualizar Contraseña'}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <button 
            onClick={() => setCurrentView('lawyer-login')}
            className="text-blue-600 hover:text-blue-800 underline text-sm"
          >
            ← Volver al login
          </button>
        </div>
      </div>
    </div>
  );
};

// CaseForm component moved outside App to prevent re-creation on re-renders
const CaseForm = ({ backendUrl, setCaseId, setShowEmailConfirmedMessage, setCurrentView, setShowEmailVerificationModal, startPolling }) => {
  const [formData, setFormData] = useState({
      client_name: '',
      firstName: '',
      apellidoPaterno: '',
      apellidoMaterno: '',
      birthDate: '',
      email: '',
      phone: '',
      phoneCountryCode: '+52', // 🆕 Código de país por defecto México
      rut: '',
      legal_area: '',
      case_description: '',
      provincia: '',
      ciudad: '',
      comuna: '',
      urgency: 'media',
      estimated_budget: '',
      consultation_expectations: '',
      decision_maker: true,
      has_legal_advice: false,
      previous_lawyer_experience: '',
      legal_problem_history: '',
      motivation: '',
      payment_capacity: 'medium',
      budget_range: '5000-15000',
      resolution_timeframe: 'medium-term',
      previous_consultations: 'no',
      contact_consent: false,
      lawyer_fees_understood: false,
      attachments: [] // Lista de archivos adjuntos
    });
  
  // 🆕 Estado para regiones activas (desde admin)
  const [activeRegions, setActiveRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(true);
  
  // 🆕 Cargar regiones activas al montar el componente
  useEffect(() => {
    const fetchActiveRegions = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/public/active-regions`);
        if (response.ok) {
          const data = await response.json();
          setActiveRegions(data.regions || []);
        }
      } catch (error) {
        console.error('Error loading active regions:', error);
        // En caso de error, usar lista completa
        setActiveRegions([
          "Aguascalientes", "Baja California", "Baja California Sur", "Campeche",
          "Chiapas", "Chihuahua", "Ciudad de México", "Coahuila", "Colima",
          "Durango", "Estado de México", "Guanajuato", "Guerrero", "Hidalgo",
          "Jalisco", "Michoacán", "Morelos", "Nayarit", "Nuevo León", "Oaxaca",
          "Puebla", "Querétaro", "Quintana Roo", "San Luis Potosí", "Sinaloa",
          "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán", "Zacatecas"
        ]);
      } finally {
        setLoadingRegions(false);
      }
    };
    fetchActiveRegions();
  }, [backendUrl]);
  
  // RUT formatting function
  // Estados mexicanos válidos para CURP
  const ESTADOS_MEXICANOS = {
    'AS': 'Aguascalientes', 'BC': 'Baja California', 'BS': 'Baja California Sur',
    'CC': 'Campeche', 'CL': 'Coahuila', 'CM': 'Colima', 'CS': 'Chiapas',
    'CH': 'Chihuahua', 'DF': 'Ciudad de México', 'DG': 'Durango',
    'GT': 'Guanajuato', 'GR': 'Guerrero', 'HG': 'Hidalgo', 'JC': 'Jalisco',
    'MC': 'México', 'MN': 'Michoacán', 'MS': 'Morelos', 'NT': 'Nayarit',
    'NL': 'Nuevo León', 'OC': 'Oaxaca', 'PL': 'Puebla', 'QT': 'Querétaro',
    'QR': 'Quintana Roo', 'SP': 'San Luis Potosí', 'SL': 'Sinaloa',
    'SR': 'Sonora', 'TC': 'Tabasco', 'TS': 'Tamaulipas', 'TL': 'Tlaxcala',
    'VZ': 'Veracruz', 'YN': 'Yucatán', 'ZS': 'Zacatecas', 'NE': 'Nacido en el Extranjero'
  };

  // 🔍 VALIDACIÓN CRUZADA COMPLETA DEL CURP
  const validateCURPWithPersonalData = (curp, firstName, apellidoPaterno, apellidoMaterno, birthDate) => {
    // Validación básica de formato primero
    const basicValidation = validateCURPFormat(curp);
    if (!basicValidation.valid) {
      return basicValidation;
    }

    // VALIDACIÓN CRUZADA 1: Nombre vs primeras 4 letras del CURP
    if (firstName && apellidoPaterno && apellidoMaterno) {
      const expectedInitials = generateCURPInitials(firstName, apellidoPaterno, apellidoMaterno);
      const actualInitials = curp.substring(0, 4);
      
      if (actualInitials !== expectedInitials) {
        return {
          valid: false,
          message: 'CURP no coincide con su identidad, favor de corregir para continuar.'
        };
      }
    }

    // VALIDACIÓN CRUZADA 2: Fecha de nacimiento vs posiciones 4-9 del CURP
    if (birthDate) {
      const expectedDatePart = formatDateForCURP(birthDate);
      const actualDatePart = curp.substring(4, 10);
      
      if (actualDatePart !== expectedDatePart) {
        return {
          valid: false,
          message: 'CURP no coincide con su identidad, favor de corregir para continuar.'
        };
      }
    }

    return {
      valid: true,
      message: '✅ CURP válido y verificado con tus datos personales',
      info: {
        sexo: curp.charAt(10) === 'H' ? 'Hombre' : 'Mujer',
        estado: ESTADOS_MEXICANOS[curp.substring(11, 13)],
        verified: true
      }
    };
  };

  // Función auxiliar: Generar iniciales para CURP
  const generateCURPInitials = (firstName, apellidoPaterno, apellidoMaterno) => {
    const cleanName = (str) => str.toUpperCase().replace(/[^A-ZÁÉÍÓÚÑÜ]/g, '');
    
    const cleanApellidoPaterno = cleanName(apellidoPaterno);
    const cleanApellidoMaterno = cleanName(apellidoMaterno);
    const cleanFirstName = cleanName(firstName.split(' ')[0]); // Solo primer nombre
    
    return (
      cleanApellidoPaterno.charAt(0) +
      (cleanApellidoPaterno.substring(1).match(/[AEIOUÁÉÍÓÚÜ]/) || ['X'])[0] +
      cleanApellidoMaterno.charAt(0) +
      cleanFirstName.charAt(0)
    );
  };

  // Función auxiliar: Formatear fecha para CURP
  const formatDateForCURP = (birthDate) => {
    const date = new Date(birthDate);
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return year + month + day;
  };

  // Validación básica de formato (función separada)
  const validateCURPFormat = (curp) => {
    if (!curp || curp.length !== 18) {
      return { valid: false, message: 'CURP debe tener exactamente 18 caracteres' };
    }

    // Validar estructura básica: primeras 4 letras
    if (!/^[A-Z]{4}/.test(curp)) {
      return { valid: false, message: 'Los primeros 4 caracteres deben ser letras' };
    }

    // Validar fecha de nacimiento (posiciones 4-9: AAMMDD)
    const dateStr = curp.substring(4, 10);
    if (!/^\d{6}$/.test(dateStr)) {
      return { valid: false, message: 'La fecha (posiciones 5-10) debe ser 6 números (AAMMDD)' };
    }

    const year = parseInt(curp.substring(4, 6));
    const month = parseInt(curp.substring(6, 8));
    const day = parseInt(curp.substring(8, 10));
    
    if (month < 1 || month > 12) {
      return { valid: false, message: 'Mes inválido en CURP (debe ser 01-12)' };
    }
    
    if (day < 1 || day > 31) {
      return { valid: false, message: 'Día inválido en CURP (debe ser 01-31)' };
    }

    // Validar sexo (posición 10: H o M)
    const sexo = curp.charAt(10);
    if (sexo !== 'H' && sexo !== 'M') {
      return { valid: false, message: 'Sexo debe ser H (hombre) o M (mujer), encontrado: ' + sexo };
    }

    // Validar estado (posiciones 11-12)
    const estado = curp.substring(11, 13);
    if (!/^[A-Z]{2}$/.test(estado)) {
      return { valid: false, message: 'Código de estado debe ser 2 letras' };
    }
    
    if (!ESTADOS_MEXICANOS[estado]) {
      return { valid: false, message: 'Código de estado inválido: ' + estado + '. Debe ser un código oficial mexicano' };
    }

    // Validar consonantes (posiciones 13-15)
    const consonantes = curp.substring(13, 16);
    if (!/^[A-Z]{3}$/.test(consonantes)) {
      return { valid: false, message: 'Las consonantes (posiciones 14-16) deben ser 3 letras' };
    }

    // Validar homoclave (posiciones 16-17)
    const homoclave = curp.substring(16, 18);
    if (!/^[A-Z0-9]{2}$/.test(homoclave)) {
      return { valid: false, message: 'La homoclave (últimos 2 caracteres) debe ser letras o números' };
    }

    return { 
      valid: true, 
      message: 'CURP con formato válido',
      info: {
        sexo: sexo === 'H' ? 'Hombre' : 'Mujer',
        estado: ESTADOS_MEXICANOS[estado],
        year: year < 50 ? 2000 + year : 1900 + year
      }
    };
  };

  // Función principal que usa la validación cruzada
  const validateCURP = (curp) => {
    return validateCURPWithPersonalData(
      curp,
      formData.firstName,
      formData.apellidoPaterno,
      formData.apellidoMaterno,
      formData.birthDate
    );
  };

  const formatRUT = (value) => {
    // Para CURP mexicano: permitir solo letras y números
    const clean = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    
    // Limit to 18 characters max (CURP length)
    const limited = clean.slice(0, 18);
    
    return limited;
  };


  // 🆕 ESTADOS PARA EXTRANJEROS SIN RFC/CURP
  const [hasMexicanID, setHasMexicanID] = useState(true); // Por defecto mexicano
  const [passportData, setPassportData] = useState({
    number: '',
    country: ''
  });

  // Estados para validación de CURP
  const [curpValidation, setCurpValidation] = useState({ valid: null, message: '', info: null });

  // CURP input handler con validación en tiempo real
  const handleRUTInput = (value, setterFunction, fieldName) => {
    const formattedCURP = formatRUT(value);
    
    // Actualizar el valor del campo
    setterFunction(prev => ({
      ...prev,
      [fieldName]: formattedCURP
    }));

    // Validar CURP si tiene 18 caracteres
    if (formattedCURP.length === 18) {
      const validation = validateCURP(formattedCURP);
      setCurpValidation(validation);
    } else if (formattedCURP.length > 0) {
      setCurpValidation({ 
        valid: null, 
        message: `Faltan ${18 - formattedCURP.length} caracteres`,
        info: null 
      });
    } else {
      setCurpValidation({ valid: null, message: '', info: null });
    }
  };

  // Estados para verificación de email en tiempo real
  const [emailVerificationState, setEmailVerificationState] = useState({
    verified: false,
    verificationSent: false,
    loading: false,
    error: null,
    canProceed: false,
    emailCheckingInProgress: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 🆕 ESTADO PARA REPUBLICACIÓN DE CASOS EXPIRADOS
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [resubmitLoading, setResubmitLoading] = useState(false);
  const [originalCaseId, setOriginalCaseId] = useState(null);

  // 🆕 DETECTAR PARÁMETROS DE REPUBLICACIÓN EN URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resubmit = urlParams.get('resubmit');
    const caseId = urlParams.get('case_id');
    
    if (resubmit === 'true' && caseId) {
      console.log('🔄 Modo republicación detectado para caso:', caseId);
      setResubmitLoading(true);
      
      // Cargar datos del caso expirado
      fetch(`${backendUrl}/api/cases/${caseId}/resubmit-data`)
        .then(response => {
          if (!response.ok) {
            throw new Error('No se pudo cargar el caso');
          }
          return response.json();
        })
        .then(result => {
          if (result.success && result.data) {
            const caseData = result.data;
            console.log('✅ Datos del caso cargados:', caseData);
            
            // Pre-llenar el formulario con los datos anteriores
            // Extraer nombre, apellidos del nombre completo si es necesario
            const nameParts = (caseData.name || '').split(' ');
            const firstName = nameParts[0] || '';
            const apellidoPaterno = nameParts[1] || '';
            const apellidoMaterno = nameParts.slice(2).join(' ') || '';
            
            setFormData(prev => ({
              ...prev,
              client_name: caseData.name || '',
              firstName: firstName,
              apellidoPaterno: apellidoPaterno,
              apellidoMaterno: apellidoMaterno,
              email: caseData.email || '',
              phone: caseData.phone || '',
              rut: caseData.rut || '',
              provincia: caseData.provincia || caseData.estado || '',
              ciudad: caseData.ciudad || '',
              comuna: caseData.comuna || '',
              legal_area: caseData.area_legal || '',
              case_description: caseData.description || '',
              urgency: caseData.urgency || 'media',
              payment_capacity: caseData.payment_capacity || 'medium',
              budget_range: caseData.budget_range || '5000-15000'
            }));
            
            setIsResubmitting(true);
            setOriginalCaseId(caseId);
            
            // Limpiar URL para evitar recargas accidentales
            window.history.replaceState({}, document.title, '/?view=case-form');
          }
        })
        .catch(error => {
          console.error('❌ Error cargando datos para republicar:', error);
          alert('No se pudieron cargar los datos del caso anterior. Por favor, complete el formulario manualmente.');
        })
        .finally(() => {
          setResubmitLoading(false);
        });
    }
  }, [backendUrl]);

  // 🆕 VALIDACIÓN DE DESCRIPCIÓN DEL CASO - Función para verificar calidad
  const validateCaseDescription = (description) => {
    const text = description?.toLowerCase() || '';
    const charCount = description?.length || 0;
    const issues = [];
    const suggestions = [];
    
    // 1. Validar longitud mínima (150 caracteres)
    if (charCount < 150) {
      issues.push(`Mínimo 150 caracteres (tienes ${charCount})`);
    }
    
    // 2. Verificar si menciona fechas o tiempo
    const hasTimeReference = /\d{4}|enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre|hace \d|semana|mes|año|días|ayer|hoy|fecha/i.test(text);
    if (!hasTimeReference) {
      issues.push('No mencionas cuándo ocurrió');
      suggestions.push('¿Cuándo pasó esto?');
    }
    
    // 3. Verificar si menciona documentos/pruebas
    const hasDocuments = /documento|contrato|recibo|factura|prueba|evidencia|carta|escrito|notificación|acta|certificado|comprobante|foto|video|mensaje|whatsapp|correo|email/i.test(text);
    if (!hasDocuments) {
      issues.push('No mencionas documentos o pruebas');
      suggestions.push('¿Qué documentos o pruebas tienes?');
    }
    
    // 4. Verificar si menciona montos (para casos que lo requieran)
    const hasMoney = /\$|\d+\.?\d*\s*(pesos|mxn|usd|mil|dólares|dolares)|monto|cantidad|dinero|pago|deuda|sueldo|salario|finiquito|liquidación|indemnización/i.test(text);
    
    // 5. Detectar área legal y dar sugerencias específicas
    const areaKeywords = {
      laboral: /despido|trabajo|empleador|empresa|sueldo|salario|finiquito|liquidación|patrón|contrato laboral|imss|infonavit|aguinaldo|vacaciones|horas extra/i,
      familia: /divorcio|custodia|pensión|matrimonio|hijo|esposo|esposa|separación|alimentos|guardia|visitas|patria potestad/i,
      civil: /contrato|deuda|préstamo|arrendamiento|renta|propiedad|daño|demanda|incumplimiento|pago/i,
      penal: /denuncia|delito|robo|fraude|amenaza|lesiones|fiscalía|ministerio público|demanda penal/i
    };
    
    let detectedArea = null;
    for (const [area, regex] of Object.entries(areaKeywords)) {
      if (regex.test(text)) {
        detectedArea = area;
        break;
      }
    }
    
    // Sugerencias específicas por área
    if (detectedArea === 'laboral' && !hasMoney) {
      suggestions.push('¿Cuánto te deben o cuánto ganabas?');
    }
    if (detectedArea === 'laboral') {
      if (!/cuánto tiempo|años|meses|antigüedad|\d+\s*(años|meses)/i.test(text)) {
        suggestions.push('¿Cuánto tiempo trabajaste ahí?');
      }
    }
    if (detectedArea === 'familia' && /divorcio/i.test(text)) {
      if (!/bienes|propiedad|casa|departamento|auto|carro/i.test(text)) {
        suggestions.push('¿Hay bienes que dividir?');
      }
    }
    
    const isValid = charCount >= 150 && hasTimeReference && hasDocuments;
    
    return {
      isValid,
      charCount,
      issues,
      suggestions,
      detectedArea,
      hasTimeReference,
      hasDocuments,
      hasMoney
    };
  };

  // Estado de validación de descripción (se actualiza reactivamente)
  const descriptionValidation = validateCaseDescription(formData.case_description);

  // Polling inteligente para detectar verificación de email automáticamente
  useEffect(() => {
    let pollingInterval;
    let pollingTimeout;
    let attempts = 0;
    let currentInterval = 5000; // Empezar con 5 segundos (menos agresivo)
    const maxAttempts = 15; // Reducir intentos máximos
    const maxInterval = 20000; // Máximo 20 segundos entre intentos
    let isPollingActive = false; // Flag para evitar polling múltiple
    
    const checkEmailVerification = async () => {
      if (!formData.email || emailVerificationState.verified || !emailVerificationState.verificationSent || emailVerificationState.emailCheckingInProgress || isPollingActive) {
        return;
      }
      
      isPollingActive = true; // Marcar polling como activo
      
      try {
        setEmailVerificationState(prev => ({ ...prev, emailCheckingInProgress: true }));
        const response = await fetch(`${backendUrl}/api/check-email-verification/${formData.email}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.verified === true) {
            // ¡Email verificado automáticamente!
            setEmailVerificationState(prev => ({ 
              ...prev, 
              verified: true, 
              emailCheckingInProgress: false,
              canProceed: true 
            }));
            
            // Limpiar polling
            clearInterval(pollingInterval);
            clearTimeout(pollingTimeout);
            
            console.log('✅ Email verificado automáticamente - formulario desbloqueado');
            return;
          }
        } else if (response.status === 429) {
          // Rate limited - incrementar intervalo más agresivamente
          console.warn('⚠️ Rate limited - reduciendo frecuencia de polling');
          currentInterval = Math.min(currentInterval * 2, maxInterval);
        }
      } catch (error) {
        console.warn('Error verificando email automáticamente:', error);
        // Incrementar intervalo en caso de error para reducir carga
        currentInterval = Math.min(currentInterval * 1.5, maxInterval);
      } finally {
        setEmailVerificationState(prev => ({ ...prev, emailCheckingInProgress: false }));
        isPollingActive = false; // Liberar el flag
      }
      
      attempts++;
      
      if (attempts >= maxAttempts) {
        clearInterval(pollingInterval);
        clearTimeout(pollingTimeout);
        console.log('⏰ Polling de email terminado - tiempo límite alcanzado');
        return;
      }
      
      // Incrementar intervalo progresivamente: 3s → 5s → 8s → 12s → 15s
      currentInterval = Math.min(currentInterval * 1.3, maxInterval);
      
      // Programar siguiente verificación con nuevo intervalo
      pollingTimeout = setTimeout(checkEmailVerification, currentInterval);
      console.log(`🔄 Próxima verificación en ${currentInterval/1000}s (intento ${attempts+1}/${maxAttempts})`);
    };
    
    // Iniciar polling solo si email fue enviado pero no verificado
    if (formData.email && emailVerificationState.verificationSent && !emailVerificationState.verified) {
      console.log('🔄 Iniciando verificación automática inteligente de email...');
      
      // Primera verificación después de 2 segundos (menos agresivo)
      setTimeout(checkEmailVerification, 2000);
      
      // Timeout de seguridad total: detener después de 5 minutos
      setTimeout(() => {
        clearInterval(pollingInterval);
        clearTimeout(pollingTimeout);
        console.log('⏰ Polling de email terminado por timeout total (5min)');
      }, 300000);
    }
    
    // Cleanup
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
      if (pollingTimeout) clearTimeout(pollingTimeout);
    };
  }, [formData.email, emailVerificationState.verificationSent, emailVerificationState.verified, backendUrl]);

    // 🔄 FUNCIONES DE VERIFICACIÓN DE EMAIL EN TIEMPO REAL
    const sendEmailVerification = async (email) => {
      try {
        setEmailVerificationState(prev => ({ ...prev, loading: true, error: null }));
        
        // 🆕 PREPARAR DATOS SEGÚN TIPO DE DOCUMENTO
        let verificationPayload = {
          email: email,
          user_type: "client"
        };

        if (hasMexicanID) {
          // MEXICANO: Usar RFC/CURP para verificación
          const rutForVerification = formData.rut;
          verificationPayload.rut = rutForVerification;
          verificationPayload.document_type = "rut";
        } else {
          // EXTRANJERO: Usar datos de pasaporte
          verificationPayload.passport_number = passportData.number;
          verificationPayload.passport_country = passportData.country;
          verificationPayload.document_type = "passport";
        }
        
        const response = await axios.post(`${backendUrl}/api/verify-email-realtime`, verificationPayload);

        if (response.status === 200) {
          setEmailVerificationState(prev => ({
            ...prev,
            loading: false,
            verificationSent: true,
            error: null
          }));
          return true;
        }
      } catch (error) {
        console.error('Error enviando verificación:', error);
        setEmailVerificationState(prev => ({
          ...prev,
          loading: false,
          error: error.response?.data?.detail || 'Error enviando verificación'
        }));
        return false;
      }
    };

    const checkEmailVerificationStatus = async (email) => {
      try {
        const response = await axios.get(`${backendUrl}/api/check-email-verification/${email}?user_type=client`);
        
        if (response.status === 200 && response.data.verified) {
          setEmailVerificationState(prev => ({
            ...prev,
            verified: true,
            canProceed: true,
            error: null
          }));
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error verificando estado:', error);
        return false;
      }
    };

    const handleEmailBlur = async (email) => {
      if (!email || email.length < 5) return;
      
      // Verificar si ya está verificado
      const isVerified = await checkEmailVerificationStatus(email);
      if (isVerified) return;
      
      // Si no está verificado, enviar verificación
      await sendEmailVerification(email);
    };

    // Escuchar mensajes de confirmación de email
    React.useEffect(() => {
      const handleMessage = (event) => {
        if (event.data.type === 'email_verified' && event.data.user_type === 'client') {
          setEmailVerificationState(prev => ({
            ...prev,
            verified: true,
            canProceed: true,
            verificationSent: true
          }));
        }
      };
      
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }, []);

    // Scroll to top when component mounts
    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

    const handleFileUpload = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('El archivo no debe superar los 5MB');
        e.target.value = '';
        return;
      }
      
      // Validar tipo de archivo
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        alert('Solo se permiten archivos PDF, JPG, PNG, DOC o DOCX');
        e.target.value = '';
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        // Guardar en formato compatible con el backend
        const attachment = {
          name: file.name,
          size: file.size,
          type: file.type,
          data: event.target.result // base64
        };
        setFormData(prev => ({
          ...prev, 
          attachments: [attachment] // Por ahora un solo archivo
        }));
      };
      reader.readAsDataURL(file);
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      // Validar email verificado PRIMERO
      if (!emailVerificationState.verified) {
        alert('Debes verificar tu email antes de enviar el caso. Revisa tu bandeja de entrada.');
        return;
      }
      
      // Validate required consents
      if (!formData.contact_consent) {
        alert('Debes aceptar que los abogados te contacten para continuar.');
        return;
      }
      
      if (!formData.lawyer_fees_understood) {
        alert('Debes confirmar que entiendes que los abogados cobran honorarios.');
        return;
      }
      
      // 🆕 VALIDACIÓN SEGÚN TIPO DE DOCUMENTO
      let requiredFields = ['client_name', 'email', 'phone', 'legal_area', 'provincia', 'case_description'];
      
      // 🆕 Validar teléfono tiene mínimo 10 dígitos
      const phoneDigits = formData.phone.replace(/\s/g, '');
      if (phoneDigits.length < 10) {
        alert('El número de WhatsApp debe tener al menos 10 dígitos');
        setIsSubmitting(false);
        return;
      }
      
      if (hasMexicanID) {
        // MEXICANO: Validar RFC/CURP
        requiredFields.push('rut');
        const missingFields = requiredFields.filter(field => !formData[field] || formData[field].toString().trim() === '');
        
        if (missingFields.length > 0) {
          alert(`Por favor completa todos los campos requeridos: ${missingFields.join(', ')}`);
          setIsSubmitting(false);
          return;
        }
      } else {
        // EXTRANJERO: Validar pasaporte y país
        if (!passportData.number || !passportData.country) {
          alert('Por favor completa el número de pasaporte y país de origen');
          setIsSubmitting(false);
          return;
        }
        
        const missingFields = requiredFields.filter(field => !formData[field] || formData[field].toString().trim() === '');
        
        if (missingFields.length > 0) {
          alert(`Por favor completa todos los campos requeridos: ${missingFields.join(', ')}`);
          setIsSubmitting(false);
          return;
        }
      }
      
      setIsSubmitting(true);
      
      try {
        // Construir número de teléfono completo con código de país
        const fullPhoneNumber = `${formData.phoneCountryCode} ${formData.phone}`.trim();
        
        // Map frontend fields to backend expected fields
        const submissionData = {
          name: formData.client_name,
          email: formData.email,
          phone: fullPhoneNumber, // 🆕 Teléfono con código de país
          rut: formData.rut,
          region: formData.provincia || 'Ciudad de México',
          provincia: formData.provincia || 'Ciudad de México', 
          ciudad: formData.ciudad || 'Ciudad de México',
          comuna: formData.comuna || 'Ciudad de México',
          area_legal: formData.legal_area,
          description: formData.case_description,
          payment_in_installments: false, // Default value
          contact_consent: formData.contact_consent,
          payment_capacity: formData.payment_capacity || 'medium',
          budget_range: formData.budget_range || '5000-15000',
          urgency: formData.urgency || 'media',
          previous_consultations: formData.previous_consultations || 'no',
          consultation_expectations: formData.consultation_expectations || 'legal-advice',
          resolution_timeframe: formData.resolution_timeframe || 'medium-term',
          legal_consent: true, // Required by backend
          attachments: formData.attachments || [] // 🆕 Incluir archivos adjuntos
        };
        
        console.log('Enviando datos:', submissionData);
        
        const response = await axios.post(`${backendUrl}/api/cases`, submissionData);
        
        if (response.status === 201 || response.status === 200) {
          setCaseId(response.data.case_id);
          
          // NUEVO FLUJO: Mostrar modal si requiere verificación
          if (response.data.show_email_modal) {
            setShowEmailVerificationModal(true);
            // Iniciar polling para verificar confirmación
            startPolling(response.data.case_id);
          } else {
            // Si el caso fue rechazado, mostrar mensaje de error
            alert(response.data.message);
          }
        }
      } catch (error) {
        console.error('Error enviando caso:', error);
        let errorMessage = 'Error enviando caso. ';
        
        if (error.response) {
          errorMessage += error.response.data?.detail || error.response.data?.message || 'Error del servidor.';
        } else {
          errorMessage += 'Revisa tu conexión a internet.';
        }
        
        alert(errorMessage);
        // Don't clear form data on error - let user retry
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8">
        <div className="max-w-4xl mx-auto p-6">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Encuentra tu Abogado Ideal</h1>
            
            {/* TRUST BAR - Compact, mobile-first */}
            <div className="flex flex-wrap gap-2 justify-center mb-5">
              <span className="inline-flex items-center text-xs font-semibold bg-green-100 text-green-800 px-3 py-1.5 rounded-full">100% Gratis para ti</span>
              <span className="inline-flex items-center text-xs font-semibold bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full">Abogados verificados</span>
              <span className="inline-flex items-center text-xs font-semibold bg-purple-100 text-purple-800 px-3 py-1.5 rounded-full">Respuesta en 24h</span>
            </div>

            {/* VALUE PROPOSITION - One liner */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Abogados calificados te contactan con su cotización.</p>
                  <p className="text-xs text-gray-500">Tú eliges con quién trabajar y a qué precio.</p>
                </div>
              </div>
            </div>
          </div>

          {/* 🆕 BANNER DE REPUBLICACIÓN */}
          {resubmitLoading && (
            <div className="bg-blue-50 border border-blue-300 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                <span className="text-blue-800 font-medium">Cargando datos de tu caso anterior...</span>
              </div>
            </div>
          )}
          
          {isResubmitting && !resubmitLoading && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-xl p-6 mb-6 shadow-sm">
              <div className="flex items-start">
                <div className="text-amber-600 text-2xl mr-4 mt-0.5">🔄</div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-amber-900 mb-2">
                    Estás republicando tu caso
                  </h3>
                  <p className="text-amber-800 text-sm mb-3">
                    Hemos pre-llenado el formulario con tus datos anteriores. 
                    <strong> Ahora es tu oportunidad de mejorar la descripción</strong> para que más abogados se interesen.
                  </p>
                  <div className="bg-white/60 rounded-lg p-3 border border-amber-200">
                    <p className="text-xs font-semibold text-amber-900 mb-1">💡 Consejos para mejorar tu caso:</p>
                    <ul className="text-xs text-amber-800 space-y-1">
                      <li>• Agrega más detalles sobre qué pasó y cuándo</li>
                      <li>• Menciona qué documentos tienes como prueba</li>
                      <li>• Especifica qué resultado esperas obtener</li>
                      <li>• Indica montos o cantidades involucradas si aplica</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-5 sm:p-8 space-y-5">
            {/* STEP 1: Case Info - Most important, first */}
            <div className="pb-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Tu problema legal</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Área Legal *</label>
                  <select
                    required
                    value={formData.legal_area}
                    onChange={(e) => setFormData({...formData, legal_area: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    data-testid="legal-area-select"
                  >
                    <option value="">Seleccionar área legal</option>
                    <option value="familia">Derecho de Familia</option>
                    <option value="laboral">Derecho Laboral</option>
                    <option value="civil">Derecho Civil</option>
                    <option value="penal">Derecho Penal</option>
                    <option value="comercial">Derecho Comercial</option>
                    <option value="inmobiliario">Derecho Inmobiliario</option>
                    <option value="tributario">Derecho Tributario</option>
                    <option value="administrativo">Derecho Administrativo</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado *</label>
                  <select
                    required
                    value={formData.provincia}
                    onChange={(e) => setFormData({...formData, provincia: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    data-testid="state-select"
                  >
                    <option value="">Seleccionar estado</option>
                    {activeRegions.map((region) => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad *</label>
                  <input
                    type="text"
                    required
                    value={formData.ciudad}
                    onChange={(e) => setFormData({...formData, ciudad: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Ej: Ciudad de México"
                    data-testid="city-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delegación / Colonia *</label>
                  <input
                    type="text"
                    required
                    value={formData.comuna}
                    onChange={(e) => setFormData({...formData, comuna: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Ej: Polanco, Roma Norte"
                    data-testid="comuna-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Describe tu caso *</label>
                <textarea
                  required
                  rows={4}
                  value={formData.case_description}
                  onChange={(e) => setFormData({...formData, case_description: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="¿Qué pasó? ¿Cuándo ocurrió? ¿Qué documentos tienes? ¿Qué resultado esperas?"
                  data-testid="case-description"
                />
                {/* Description quality hint */}
                {formData.case_description && formData.case_description.length < 50 && (
                  <p className="text-xs text-amber-600 mt-1">Agrega más detalles para mejores resultados</p>
                )}
                {formData.case_description && formData.case_description.length >= 50 && (
                  <p className="text-xs text-green-600 mt-1">Buena descripción</p>
                )}
              </div>
            </div>

            {/* STEP 2: Contact Info */}
            <div className="pb-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Tus datos de contacto</h3>
              
              {/* Name fields - compact row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nombre(s) *</label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({...formData, firstName: value, client_name: `${value} ${formData.apellidoPaterno} ${formData.apellidoMaterno}`.trim()});
                    }}
                    placeholder="María Elena"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Apellido Paterno *</label>
                  <input
                    type="text"
                    required
                    value={formData.apellidoPaterno}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({...formData, apellidoPaterno: value, client_name: `${formData.firstName} ${value} ${formData.apellidoMaterno}`.trim()});
                    }}
                    placeholder="García"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="paterno-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Apellido Materno *</label>
                  <input
                    type="text"
                    required
                    value={formData.apellidoMaterno}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({...formData, apellidoMaterno: value, client_name: `${formData.firstName} ${formData.apellidoPaterno} ${value}`.trim()});
                    }}
                    placeholder="López"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="materno-input"
                  />
                </div>
              </div>

              {/* Email with verification */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
                <div className="relative">
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    onBlur={(e) => handleEmailBlur(e.target.value)}
                    disabled={emailVerificationState.verified}
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:border-transparent ${
                      emailVerificationState.verified 
                        ? 'border-green-500 bg-green-50 focus:ring-green-500' 
                        : emailVerificationState.error
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="tu.email@ejemplo.com"
                    data-testid="email-input"
                  />
                  {emailVerificationState.loading && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    </div>
                  )}
                  {emailVerificationState.verified && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500 text-sm">Verificado</div>
                  )}
                </div>
                {emailVerificationState.verificationSent && !emailVerificationState.verified && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 flex items-center">
                    <svg className="w-3 h-3 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
                    <span>Revisa tu bandeja y confirma tu email para continuar</span>
                    {emailVerificationState.emailCheckingInProgress && (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500 ml-2"></div>
                    )}
                  </div>
                )}
                {emailVerificationState.verified && (
                  <p className="text-xs text-green-600 mt-1">Email verificado</p>
                )}
                {emailVerificationState.error && (
                  <p className="text-xs text-red-600 mt-1">{emailVerificationState.error}</p>
                )}
              </div>

              {/* WhatsApp */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">WhatsApp *</label>
                <div className="flex">
                  <select
                    value={formData.phoneCountryCode}
                    onChange={(e) => setFormData({...formData, phoneCountryCode: e.target.value})}
                    className="border border-gray-300 rounded-l-lg px-2 py-2.5 bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="phone-country-code"
                  >
                    <option value="+52">+52</option>
                    <option value="+1">+1</option>
                    <option value="+502">+502</option>
                    <option value="+57">+57</option>
                    <option value="+54">+54</option>
                    <option value="+34">+34</option>
                    <option value="+56">+56</option>
                    <option value="+51">+51</option>
                    <option value="+593">+593</option>
                    <option value="+58">+58</option>
                  </select>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d\s]/g, '');
                      setFormData({...formData, phone: value});
                    }}
                    className="flex-1 border border-l-0 border-gray-300 rounded-r-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="55 1234 5678"
                    data-testid="phone-input"
                  />
                </div>
                {formData.phone && formData.phone.replace(/\s/g, '').length >= 10 && (
                  <p className="text-xs text-green-600 mt-1">Número válido</p>
                )}
                {formData.phone && formData.phone.replace(/\s/g, '').length > 0 && formData.phone.replace(/\s/g, '').length < 10 && (
                  <p className="text-xs text-amber-600 mt-1">Mínimo 10 dígitos</p>
                )}
                <p className="text-xs text-gray-400 mt-1">Los abogados te contactarán por este WhatsApp</p>
              </div>

              {/* Birth date - REQUIRED */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">Fecha de Nacimiento *</label>
                <input
                  type="date"
                  required
                  value={formData.birthDate}
                  onChange={(e) => setFormData({...formData, birthDate: e.target.value})}
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:border-transparent ${
                    formData.birthDate ? 'border-green-400 focus:ring-green-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  max={new Date().toISOString().split('T')[0]}
                  data-testid="birthdate-input"
                />
                {!formData.birthDate && (
                  <p className="text-xs font-semibold text-red-600 mt-1">Requerido para verificar tu identidad</p>
                )}
              </div>

              {/* Document type - REQUIRED */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-2">Documento de Identidad *</label>
                <div className="flex gap-4 text-sm mb-3">
                  <label className="flex items-center cursor-pointer">
                    <input type="radio" name="documentType" checked={hasMexicanID} onChange={() => { setHasMexicanID(true); setPassportData({ number: '', country: '' }); }} className="mr-2 h-4 w-4 text-blue-600" />
                    <span className="text-gray-700">RFC/CURP mexicano</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="radio" name="documentType" checked={!hasMexicanID} onChange={() => { setHasMexicanID(false); setFormData(prev => ({ ...prev, rut: '' })); }} className="mr-2 h-4 w-4 text-blue-600" />
                    <span className="text-gray-700">Extranjero</span>
                  </label>
                </div>

                {hasMexicanID ? (
                  <div>
                    <input
                      type="text"
                      required
                      value={formData.rut}
                      onChange={(e) => handleRUTInput(e.target.value, setFormData, 'rut')}
                      className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:border-transparent ${
                        formData.rut && curpValidation.valid === true ? 'border-green-400 focus:ring-green-500' 
                        : formData.rut && curpValidation.valid === false ? 'border-red-400 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="PEGJ850315HDFRRN03"
                      data-testid="curp-input"
                    />
                    {!formData.rut && (
                      <p className="text-xs font-semibold text-red-600 mt-1">CURP obligatorio para continuar</p>
                    )}
                    {formData.rut && curpValidation.valid === true && (
                      <p className="text-xs text-green-600 mt-1">CURP verificado{curpValidation.info ? ` - ${curpValidation.info.sexo} - ${curpValidation.info.estado}` : ''}</p>
                    )}
                    {formData.rut && curpValidation.valid === false && (
                      <p className="text-xs text-red-500 mt-1">{curpValidation.message}</p>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <input
                        type="text"
                        required
                        value={passportData.number}
                        onChange={(e) => setPassportData(prev => ({ ...prev, number: e.target.value.toUpperCase() }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="No. Pasaporte"
                        data-testid="passport-input"
                      />
                      {!passportData.number && (
                        <p className="text-xs font-semibold text-red-600 mt-1">Requerido</p>
                      )}
                    </div>
                    <div>
                      <select
                        required
                        value={passportData.country}
                        onChange={(e) => setPassportData(prev => ({ ...prev, country: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        data-testid="passport-country"
                      >
                        <option value="">País...</option>
                        <option value="Argentina">Argentina</option>
                        <option value="Colombia">Colombia</option>
                        <option value="Ecuador">Ecuador</option>
                        <option value="Estados Unidos">Estados Unidos</option>
                        <option value="España">España</option>
                        <option value="Perú">Perú</option>
                        <option value="Venezuela">Venezuela</option>
                        <option value="Otro">Otro</option>
                      </select>
                      {!passportData.country && (
                        <p className="text-xs font-semibold text-red-600 mt-1">Requerido</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Documents + Consents + Submit - Compact */}
            <div className="space-y-4">
              {/* Documents - optional compact */}
              <details className="border border-gray-200 rounded-lg" data-testid="documents-accordion">
                <summary className="px-4 py-2.5 cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-900 select-none list-none [&::-webkit-details-marker]:hidden flex justify-between items-center">
                  <span>Adjuntar documentos (opcional)</span>
                  <svg className="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                </summary>
                <div className="px-4 pb-4">
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="file-upload"
                  />
                  <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, DOC. Máximo 5MB.</p>
                  {formData.attachments && formData.attachments.length > 0 && (
                    <div className="mt-2 text-xs text-green-600">{formData.attachments.length} archivo(s) adjuntado(s)</div>
                  )}
                </div>
              </details>

              {/* Consents - compact */}
              <div className="space-y-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    checked={formData.lawyer_fees_understood}
                    onChange={(e) => setFormData({...formData, lawyer_fees_understood: e.target.checked})}
                    className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0"
                    data-testid="fees-consent"
                  />
                  <span className="text-xs text-gray-600">
                    <strong>Entiendo que los abogados no son gratuitos.</strong> Me contactarán con sus honorarios. Usar AyudaLegal es sin costo para mí.
                  </span>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    checked={formData.contact_consent}
                    onChange={(e) => setFormData({...formData, contact_consent: e.target.checked})}
                    className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0"
                    data-testid="contact-consent"
                  />
                  <span className="text-xs text-gray-600">
                    Acepto que abogados especializados me contacten por WhatsApp, email o teléfono.
                  </span>
                </label>
              </div>

              {/* Submit button */}
              {(() => {
                const missingFields = [];
                if (!formData.legal_area) missingFields.push('Área legal');
                if (!formData.provincia) missingFields.push('Estado');
                if (!formData.ciudad) missingFields.push('Ciudad');
                if (!formData.comuna) missingFields.push('Delegación/Colonia');
                if (!formData.case_description || formData.case_description.length < 20) missingFields.push('Descripción del caso');
                if (!formData.firstName) missingFields.push('Nombre');
                if (!formData.apellidoPaterno) missingFields.push('Apellido paterno');
                if (!formData.apellidoMaterno) missingFields.push('Apellido materno');
                if (!formData.email) missingFields.push('Email');
                if (!formData.phone || formData.phone.replace(/\s/g, '').length < 10) missingFields.push('WhatsApp');
                if (!formData.birthDate) missingFields.push('Fecha de nacimiento');
                if (hasMexicanID && !formData.rut) missingFields.push('CURP');
                if (hasMexicanID && formData.rut && curpValidation.valid === false) missingFields.push('CURP válido');
                if (!hasMexicanID && !passportData.number) missingFields.push('Pasaporte');
                if (!hasMexicanID && !passportData.country) missingFields.push('País');
                if (!formData.lawyer_fees_understood) missingFields.push('Consentimiento de honorarios');
                if (!formData.contact_consent) missingFields.push('Consentimiento de contacto');
                
                const canSubmit = missingFields.length === 0 && !isSubmitting && descriptionValidation.isValid;
                
                return (
                  <>
                    {missingFields.length > 0 && formData.case_description?.length > 0 && (
                      <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 mb-3" data-testid="missing-fields-warning">
                        <p className="text-sm font-bold text-red-800 mb-1">Completa para continuar:</p>
                        <p className="text-sm text-red-700 font-medium">{missingFields.join(' · ')}</p>
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className={`w-full py-4 px-6 rounded-xl font-bold text-base sm:text-lg transition-all duration-300 ${
                        !canSubmit
                          ? 'bg-gray-200 border-2 border-gray-300 cursor-not-allowed text-gray-500' 
                          : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-lg hover:shadow-xl'
                      }`}
                      data-testid="submit-case-btn"
                    >
                      {isSubmitting 
                        ? 'Enviando tu caso...' 
                        : !canSubmit 
                          ? 'Completa todos los campos para enviar'
                          : 'Enviar caso gratis'
                      }
                    </button>
                  </>
                );
              })()}

              {/* Social proof under button */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-xs font-semibold text-gray-900">3 abogados</p>
                  <p className="text-[10px] text-gray-500">te contactarán</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-xs font-semibold text-gray-900">24-48h</p>
                  <p className="text-[10px] text-gray-500">tiempo de respuesta</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-xs font-semibold text-gray-900">100% gratis</p>
                  <p className="text-[10px] text-gray-500">sin compromiso</p>
                </div>
              </div>
            </div>
          </form>
          
          <div className="mt-6 text-center">
            <button 
              onClick={() => setCurrentView('home')}
              className="text-blue-600 hover:text-blue-800 underline text-sm font-semibold"
              data-testid="back-to-home"
            >
              Volver al Inicio
            </button>
          </div>
        </div>
      </div>
  );
};

// LawyerAuth component moved outside App to prevent re-creation on re-renders
const LawyerAuth = ({ backendUrl, setCurrentView, initialMode = 'login' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authMode, setAuthMode] = useState(initialMode); // 'login' or 'register'
    
    // Registration form states
    const [regData, setRegData] = useState({
      name: '',
      email: '',
      password: '',
      rut: '',
      areas_legales: [],
      region: '', // Changed from comunas to region
      description: '',
      phone: '',
      university: '',
      graduation_year: '',
      run_inscription_number: '',
      cedula_photo: '',
      title_certificate: ''
    });



    // RUT formatting function
    const formatRUT = (value) => {
      // Remove all non-alphanumeric characters
      const clean = value.replace(/[^0-9kK]/g, '').toUpperCase();
      
      // Limit to 9 characters max (8 digits + 1 check digit)
      const limited = clean.slice(0, 9);
      
      if (limited.length === 0) return '';
      
      // If only 1-2 characters, return as is
      if (limited.length <= 2) return limited;
      
      // Split into number part and check digit
      const numberPart = limited.slice(0, -1);
      const checkDigit = limited.slice(-1);
      
      // Format the number part with dots
      let formatted = '';
      const reversed = numberPart.split('').reverse();
      
      for (let i = 0; i < reversed.length; i++) {
        if (i > 0 && i % 3 === 0) {
          formatted = '.' + formatted;
        }
        formatted = reversed[i] + formatted;
      }
      
      // Add hyphen and check digit
      return formatted + (limited.length > 1 ? '-' + checkDigit : '');
    };

    // RUT input handler SIMPLE para abogados (solo formateo, sin pixelación)
    const handleRUTInput = (value, setterFunction, fieldName) => {
      const formattedRUT = formatRUT(value);
      setterFunction(prev => ({
        ...prev,
        [fieldName]: formattedRUT
      }));
    };

    // New states for dropdown data
    const [availableRegions, setAvailableRegions] = useState([]);
    const [availableLegalAreas, setAvailableLegalAreas] = useState([]);
    const [universities, setUniversities] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(true);

    // File upload states
    const [cedulaPreview, setCedulaPreview] = useState(null);
    const [certificatePreview, setCertificatePreview] = useState(null);
    
    // Success modal states
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState({
      title: '',
      message: '',
      isVerified: false,
      score: 0
    });

    // File upload handlers - ULTRA SAFE VERSION (No DOM Conflicts)
    const handleFileUpload = (e, fieldName) => {
      const file = e.target.files[0];
      if (!file) return;

      console.log(`🔄 Processing: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);

      // Basic validations
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        alert('Solo se permiten archivos JPG, PNG o PDF');
        e.target.value = '';
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        alert('El archivo no debe superar los 2MB');
        e.target.value = '';
        return;
      }

      // SAFE state update - prevent React DOM conflicts
      const safeSetPreview = (fieldName, value) => {
        // Use React's batch updates to prevent race conditions
        if (fieldName === 'cedula_photo') {
          setCedulaPreview(prev => value);
        } else if (fieldName === 'title_certificate') {
          setCertificatePreview(prev => value);
        }
      };

      // Set loading immediately with safety checks
      safeSetPreview(fieldName, 'LOADING');

      // Ultra-safe file processing
      const processFileSafely = () => {
        try {
          const reader = new FileReader();
          
          reader.onload = function(event) {
            try {
              const result = event.target.result;
              
              if (!result || result.length < 50) {
                throw new Error('Archivo inválido o corrupto');
              }

              // Safe state updates with error boundaries
              setTimeout(() => {
                try {
                  // Update form data safely - Extract only base64 part
                  const base64Data = result.includes(',') ? result.split(',')[1] : result;
                  
                  setRegData(prevData => ({
                    ...prevData,
                    [fieldName]: base64Data
                  }));

                  // Update preview safely
                  if (file.type.startsWith('image/')) {
                    safeSetPreview(fieldName, result);
                  } else {
                    safeSetPreview(fieldName, 'PDF_UPLOADED');
                  }

                  console.log(`✅ File uploaded: ${fieldName}`);

                } catch (stateError) {
                  console.error('State update error:', stateError);
                  handleUploadError(fieldName, e, 'Error actualizando estado');
                }
              }, 50); // Small delay to prevent React conflicts

            } catch (processError) {
              console.error('Processing error:', processError);
              handleUploadError(fieldName, e, 'Error procesando archivo');
            }
          };

          reader.onerror = function() {
            handleUploadError(fieldName, e, 'Error leyendo archivo');
          };

          reader.onabort = function() {
            handleUploadError(fieldName, e, 'Carga cancelada');
          };

          // Start reading
          reader.readAsDataURL(file);

        } catch (readerError) {
          console.error('Reader setup error:', readerError);
          handleUploadError(fieldName, e, 'Error inicializando lector');
        }
      };

      // Error handler
      const handleUploadError = (fieldName, event, message) => {
        console.error(`❌ Upload error (${fieldName}):`, message);
        alert(message);
        
        // Safe cleanup
        try {
          event.target.value = '';
        } catch (e) {
          console.warn('Could not clear input');
        }
        
        // Safe state reset
        safeSetPreview(fieldName, null);
      };

      // Start processing with minimal delay
      setTimeout(processFileSafely, 10);
    };

    // Load dropdown options from backend
    useEffect(() => {
      const loadDropdownOptions = async () => {
        try {
          const [regionsResponse, legalAreasResponse, universitiesResponse] = await Promise.all([
            fetch(`${backendUrl}/api/regions`),
            fetch(`${backendUrl}/api/legal-areas`),
            fetch(`${backendUrl}/api/universities`)
          ]);

          if (regionsResponse.ok && legalAreasResponse.ok && universitiesResponse.ok) {
            const regionsData = await regionsResponse.json();
            const legalAreasData = await legalAreasResponse.json();
            const universitiesData = await universitiesResponse.json();
            
            setAvailableRegions(regionsData.regions || []);
            setAvailableLegalAreas(legalAreasData.legal_areas || []);
            setUniversities(universitiesData.universities || []);
          }
        } catch (error) {
          console.error('Error loading dropdown options:', error);
          // Fallback data
          setAvailableRegions(['Ciudad de México', 'Todo México']);
          setAvailableLegalAreas(['Familia', 'Civil', 'Laboral', 'Penal', 'Comercial']);
          setUniversities(['Universidad Nacional Autónoma de México', 'Instituto Tecnológico y de Estudios Superiores de Monterrey']);
        } finally {
          setLoadingOptions(false);
        }
      };

      loadDropdownOptions();
    }, [backendUrl]);

    const handleLogin = async (e) => {
      e.preventDefault();
      setIsLoading(true);
      try {
        const response = await axios.post(`${backendUrl}/api/lawyers/login`, {
          email: email,
          password: password
        });
        if (response.status === 200) {
          // El backend devuelve 'token', no 'access_token'
          const token = response.data.token;
          const lawyerData = response.data.lawyer;
          
          localStorage.setItem('lawyer_token', token);
          localStorage.setItem('lawyer_data', JSON.stringify(lawyerData));
          localStorage.setItem('lawyer_id', lawyerData.id);
          
          console.log('Login successful, token saved:', token ? 'YES' : 'NO');
          setCurrentView('lawyer-dashboard');
        }
      } catch (error) {
        console.error('Login error:', error);
        alert('Error en login: ' + (error.response?.data?.detail || error.message));
      }
      setIsLoading(false);
    };

    const handleRegister = async (e) => {
      e.preventDefault();
      
      // Validation checks
      if (regData.areas_legales.length === 0) {
        alert('Debe seleccionar al menos un área legal');
        return;
      }
      
      if (regData.description.length < 100) {
        alert('La descripción profesional debe tener al menos 100 caracteres');
        return;
      }
      
      if (!regData.cedula_photo) {
        alert('Debe subir la foto de su cédula de identidad');
        return;
      }
      
      if (!regData.title_certificate) {
        alert('Debe subir su certificado de título profesional');
        return;
      }
      
      setIsLoading(true);
      try {
        // Prepare data for backend
        const registrationData = {
          ...regData,
          comunas: [regData.region] // Backend expects comunas array, sending region
        };
        
        const response = await axios.post(`${backendUrl}/api/lawyers/register`, registrationData);
        if (response.status === 201) {
          // Show professional success message - SIEMPRE revisión manual
          const responseData = response.data;
          const verificationData = responseData.verification;
          
          // Todos los abogados van a revisión manual del admin
          setSuccessMessage({
            title: '¡Registro Exitoso!',
            message: 'Tu solicitud ha sido recibida y está en proceso de revisión. Te contactaremos por email dentro de las próximas 24 horas con el resultado de la evaluación.',
            isVerified: false,  // Nunca auto-verificados
            score: verificationData.score
          });
          setShowSuccessModal(true);
        }
      } catch (error) {
        console.error('Registration error:', error);
        const errorMessage = error.response?.data?.detail || error.message;
        alert('Error en registro: ' + errorMessage);
      }
      setIsLoading(false);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-4">
        {authMode === 'login' ? (
          // LOGIN FORM
          <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-2xl border border-gray-200">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Portal Abogados</h2>
              <p className="text-gray-600 mt-2">Accede a casos verificados por IA</p>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                <div className="text-green-700 font-semibold text-sm">
                  🎁 <strong>PRIMER CASO GRATIS</strong> para nuevos abogados
                </div>
              </div>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  placeholder="tu.email@ejemplo.com"
                  autoComplete="email"
                  disabled={isLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 disabled:opacity-50"
              >
                {isLoading ? 'Iniciando...' : 'Iniciar Sesión'}
              </button>
            </form>
            
            {/* Forgot Password Link */}
            <div className="mt-4 text-center">
              <button 
                onClick={() => setCurrentView('forgot-password')}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                ¿No tienes cuenta?{' '}
                <button 
                  onClick={() => setAuthMode('register')}
                  className="text-blue-600 hover:text-blue-800 font-semibold"
                >
                  Regístrate aquí
                </button>
              </p>
            </div>
            
            <div className="mt-6 text-center">
              <button 
                onClick={() => setCurrentView('home')}
                className="text-gray-500 hover:text-gray-700"
              >
                ← Volver al Inicio
              </button>
            </div>
          </div>
        ) : (
          // REGISTRATION FORM
          <div className="max-w-2xl w-full bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-200">
            
            {/* 🆕 BLOQUE SUPERIOR MARKETING CON IA */}
            <div className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 border border-purple-200 rounded-2xl p-4 sm:p-6 mb-6 shadow-lg">
              <div className="text-center">
                <div className="text-3xl mb-3">🧠</div>
                <h1 className="text-xl sm:text-2xl font-bold text-purple-900 mb-3 leading-tight">
                  Plataforma con IA para abogados inteligentes
                </h1>
                <p className="text-base sm:text-lg text-purple-800 font-medium mb-4">
                  Nuestra IA analiza cada caso en 6 pasos antes de enviártelo.
                </p>
                
                {/* BENEFICIOS DESTACADOS */}
                <div className="bg-white bg-opacity-70 rounded-xl p-3 sm:p-4 space-y-2">
                  <div className="flex items-center justify-center text-sm sm:text-base">
                    <span className="text-2xl mr-3">🎁</span>
                    <span className="text-purple-900 font-bold">Incluye 1 crédito gratuito para conectar con tu primer caso real.</span>
                  </div>
                  <div className="flex items-center justify-center text-sm sm:text-base">
                    <span className="text-green-600 mr-2">✅</span>
                    <span className="text-purple-800 font-medium">Sin mensualidades. Solo pagas por lo que usas.</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Registro de Abogados</h2>
              <p className="text-gray-600 mt-2">Únete a nuestra red profesional</p>
              
              {/* 🆕 SUBTÍTULO MEJORADO MARKETING */}
              <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-4 mt-4 shadow-sm">
                <div className="text-center">
                  <div className="text-emerald-700 font-bold text-base sm:text-lg mb-1">
                    🎁 <strong>Verificación gratuita + Crédito inicial incluido</strong>
                  </div>
                  <div className="text-emerald-600 text-sm">
                    Solo abogados verificados pueden acceder a casos filtrados por IA.
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleRegister} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre completo según documento de identidad *
                  </label>
                  <input
                    type="text"
                    required
                    value={regData.name}
                    onChange={(e) => setRegData({...regData, name: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ingrese su nombre tal como aparece en su cédula de identidad"
                    disabled={isLoading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Debe coincidir exactamente con su identificación oficial mexicana
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Profesional *</label>
                  <input
                    type="email"
                    required
                    value={regData.email}
                    onChange={(e) => setRegData({...regData, email: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="nombre@bufete.com"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña *</label>
                  <input
                    type="password"
                    required
                    value={regData.password}
                    onChange={(e) => setRegData({...regData, password: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Contraseña segura"
                    disabled={isLoading}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono *</label>
                  <input
                    type="tel"
                    required
                    value={regData.phone}
                    onChange={(e) => setRegData({...regData, phone: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+56 9 1234 5678"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">RFC o CURP *</label>
                  <input
                    type="text"
                    required
                    value={regData.rut}
                    onChange={(e) => handleRUTInput(e.target.value, setRegData, 'rut')}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="GODE561231GR8 o MEXG800825HDCRNL09"
                    disabled={isLoading}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Universidad *</label>
                  <select
                    required
                    value={regData.university}
                    onChange={(e) => setRegData({...regData, university: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    disabled={isLoading || loadingOptions}
                  >
                    <option value="">Seleccione su universidad</option>
                    {universities.map((university, index) => (
                      <option key={index} value={university}>
                        {university}
                      </option>
                    ))}
                  </select>
                  {loadingOptions && (
                    <p className="text-sm text-gray-500 mt-1">Cargando universidades...</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Año de Graduación *</label>
                  <input
                    type="number"
                    required
                    value={regData.graduation_year}
                    onChange={(e) => setRegData({...regData, graduation_year: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="2020"
                    min="1980"
                    max="2024"
                    disabled={isLoading}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    N° Registro Colegio 
                    <span className="text-gray-500 text-xs ml-1">(opcional - solo si está inscrito)</span>
                  </label>
                  <input
                    type="text"
                    value={regData.run_inscription_number}
                    onChange={(e) => setRegData({...regData, run_inscription_number: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ej: 12345 (dejar vacío si no está inscrito)"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Áreas legales que maneja *
                </label>
                <div className="border border-gray-300 rounded-lg px-4 py-3 max-h-48 overflow-y-auto">
                  {loadingOptions ? (
                    <p className="text-gray-500">Cargando áreas legales...</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {availableLegalAreas.map((area) => (
                        <label key={area} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={regData.areas_legales.includes(area)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setRegData({
                                  ...regData, 
                                  areas_legales: [...regData.areas_legales, area]
                                });
                              } else {
                                setRegData({
                                  ...regData, 
                                  areas_legales: regData.areas_legales.filter(a => a !== area)
                                });
                              }
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            disabled={isLoading}
                          />
                          <span className="text-sm text-gray-700">{area}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Seleccione todas las áreas en las que tiene experiencia
                </p>
                {regData.areas_legales.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">
                    Debe seleccionar al menos un área legal
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado donde atiende *
                </label>
                <select
                  required
                  value={regData.region}
                  onChange={(e) => setRegData({...regData, region: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoading || loadingOptions}
                >
                  <option value="">Seleccione estado donde atenderá</option>
                  {availableRegions.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Seleccione "Todo México" si atiende en todo el país
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción profesional *
                </label>
                <textarea
                  required
                  value={regData.description}
                  onChange={(e) => setRegData({...regData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent h-32 resize-none"
                  placeholder="Ej: Abogado especialista en derecho de familia con más de 10 años de experiencia en divorcios, custodia de menores y pensiones alimenticias. Atiendo en mi oficina en Las Condes y ofrezco consultas virtuales."
                  disabled={isLoading}
                  minLength="100"
                  maxLength="500"
                />
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-gray-500">
                    Destaque su experiencia, especialidades y métodos de atención
                  </p>
                  <span className={`text-xs ${
                    regData.description.length < 100 ? 'text-red-500' : 
                    regData.description.length > 450 ? 'text-yellow-500' : 'text-green-500'
                  }`}>
                    {regData.description.length}/500
                  </span>
                </div>
                {regData.description.length < 100 && (
                  <p className="text-xs text-red-500 mt-1">
                    Mínimo 100 caracteres para una descripción completa
                  </p>
                )}
              </div>

              {/* Document Upload Section */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📄 Documentos de Verificación</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Cédula Photo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Foto frontal de cédula de identidad *
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,application/pdf"
                        onChange={(e) => handleFileUpload(e, 'cedula_photo')}
                        className="hidden"
                        id="cedula-upload"
                        disabled={isLoading}
                      />
                      <label 
                        htmlFor="cedula-upload" 
                        className="cursor-pointer"
                      >
                        {cedulaPreview === 'LOADING' ? (
                          <div key="loading">
                            <div className="mx-auto h-12 w-12 text-blue-500 animate-spin mb-2">
                              <svg className="h-full w-full" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </div>
                            <p className="text-blue-600 text-sm">📄 Procesando...</p>
                          </div>
                        ) : cedulaPreview === 'PDF_UPLOADED' ? (
                          <div key="pdf">
                            <div className="mx-auto h-12 w-12 text-red-600 mb-2">
                              <svg className="h-full w-full" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                              </svg>
                            </div>
                            <p className="text-green-600 text-sm">✅ PDF cargado</p>
                          </div>
                        ) : cedulaPreview && cedulaPreview.startsWith('data:image') ? (
                          <div key="image">
                            <img 
                              src={cedulaPreview} 
                              alt="Preview cédula" 
                              className="max-h-32 mx-auto mb-2 rounded border"
                            />
                            <p className="text-green-600 text-sm">✅ Imagen cargada</p>
                          </div>
                        ) : (
                          <div>
                            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <p className="mt-2 text-sm text-gray-600">
                              <span className="font-medium text-blue-600">Haz clic para subir</span> o arrastra aquí
                            </p>
                            <p className="text-xs text-gray-500">PNG, JPG o PDF hasta 2MB</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Certificate Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Certificado de título profesional *
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,application/pdf"
                        onChange={(e) => handleFileUpload(e, 'title_certificate')}
                        className="hidden"
                        id="certificate-upload"
                        disabled={isLoading}
                      />
                      <label 
                        htmlFor="certificate-upload" 
                        className="cursor-pointer"
                      >
                        {certificatePreview === 'LOADING' ? (
                          <div key="loading">
                            <div className="mx-auto h-12 w-12 text-blue-500 animate-spin mb-2">
                              <svg className="h-full w-full" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </div>
                            <p className="text-blue-600 text-sm">📄 Procesando...</p>
                          </div>
                        ) : certificatePreview === 'PDF_UPLOADED' ? (
                          <div key="pdf">
                            <div className="mx-auto h-12 w-12 text-red-600 mb-2">
                              <svg className="h-full w-full" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                              </svg>
                            </div>
                            <p className="text-green-600 text-sm">✅ PDF cargado</p>
                          </div>
                        ) : certificatePreview && certificatePreview.startsWith('data:image') ? (
                          <div key="image">
                            <img 
                              src={certificatePreview} 
                              alt="Preview certificado" 
                              className="max-h-32 mx-auto mb-2 rounded border"
                            />
                            <p className="text-green-600 text-sm">✅ Imagen cargada</p>
                          </div>
                        ) : (
                          <div>
                            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                              <path d="M9 12h6m6 0h6m-6 6v6m6-6v6m-12 0v6m6-6v6" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M21 21.5a.5.5 0 11-1 0 .5.5 0 011 0zM27 21.5a.5.5 0 11-1 0 .5.5 0 011 0z"/>
                            </svg>
                            <p className="mt-2 text-sm text-gray-600">
                              <span className="font-medium text-blue-600">Haz clic para subir</span> o arrastra aquí
                            </p>
                            <p className="text-xs text-gray-500">PNG, JPG o PDF hasta 2MB</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 mt-3">
                  💡 Estos documentos son requeridos para verificar su identidad profesional
                </p>
              </div>

              {/* 🆕 TEXTO PRE-BOTÓN MOTIVACIONAL */}
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-4 mb-4 shadow-sm">
                <div className="text-center">
                  <div className="flex items-center justify-center text-base font-bold text-yellow-900 mb-2">
                    <span className="text-green-600 mr-2">✅</span>
                    Al registrarte, activas 1 crédito gratuito para conectar con un caso real.
                  </div>
                  <p className="text-sm text-yellow-800 italic">
                    Nuestra IA filtra cada caso según urgencia, especialidad y zona. Solo recibes lo que realmente puedes ganar.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-green-700 hover:to-green-800 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 shadow-lg"
              >
                {isLoading ? '🔄 Registrando...' : '🚀 Crear Cuenta de Abogado'}
              </button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                ¿Ya tienes cuenta?{' '}
                <button 
                  onClick={() => setAuthMode('login')}
                  className="text-blue-600 hover:text-blue-800 font-semibold"
                >
                  Inicia sesión aquí
                </button>
              </p>
            </div>
            
            <div className="mt-6 text-center">
              <button 
                onClick={() => setCurrentView('home')}
                className="text-gray-500 hover:text-gray-700"
              >
                ← Volver al Inicio
              </button>
            </div>
          </div>
        )}
        
        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
              {/* Close button */}
              <button 
                onClick={() => {
                  setShowSuccessModal(false);
                  setAuthMode('login');
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
              
              {/* Success icon - siempre amarillo para revisión manual */}
              <div className="text-center mb-6">
                <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-yellow-100">
                  <span className="text-3xl">⏳</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {successMessage.title}
                </h3>
              </div>
              
              {/* Message */}
              <div className="text-center mb-8">
                <p className="text-gray-600 text-lg leading-relaxed">
                  {successMessage.message}
                </p>
                
                {/* Additional instructions - siempre revisión manual */}
                <div className="mt-6 p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                  <div className="text-sm">
                    <p className="font-semibold text-yellow-800">
                      📋 ¿Qué pasará ahora?
                    </p>
                    <ul className="mt-2 text-left space-y-1 text-yellow-700">
                      <li>• Nuestro equipo revisará tus documentos cuidadosamente</li>
                      <li>• Te contactaremos dentro de 24 horas máximo</li>
                      <li>• Revisa tu email incluyendo la carpeta de spam</li>
                      <li>• Evaluaremos tu score: {successMessage.score}/100</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Action buttons - solo botón para ir al inicio */}
              <div className="flex flex-col space-y-3">
                <button 
                  onClick={() => {
                    setShowSuccessModal(false);
                    setCurrentView('home');
                  }}
                  className="px-6 py-3 rounded-xl font-semibold transition-colors bg-blue-600 text-white hover:bg-blue-700"
                >
                  Ir a la Página Principal
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

// Modal de Verificación de Email (componente separado)
const EmailVerificationModal = ({ showEmailVerificationModal, closeModal, caseStatus }) => {
  if (!showEmailVerificationModal) return null;
  
  return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
              {/* Close button */}
              <button 
                onClick={closeModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
              
              {/* Email icon */}
              <div className="text-center mb-6">
                <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-blue-100">
                  <span className="text-3xl">📧</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Confirma tu Email
                </h3>
              </div>
              
              {/* Message */}
              <div className="text-center mb-8">
                <p className="text-gray-600 text-lg leading-relaxed mb-4">
                  Hemos enviado un email de confirmación a tu correo.
                </p>
                
                <div className="mt-6 p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="text-sm">
                    <p className="font-semibold text-blue-800 mb-2">
                      📋 Pasos a seguir:
                    </p>
                    <ul className="text-left space-y-1 text-blue-700">
                      <li>• Revisa tu bandeja de entrada</li>
                      <li>• Busca también en spam/promociones</li>
                      <li>• Haz clic en el enlace de confirmación</li>
                      <li>• Tu caso se enviará automáticamente a los abogados</li>
                    </ul>
                  </div>
                </div>
                
                {caseStatus === 'pending_verification' && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin h-5 w-5 border-2 border-yellow-600 border-t-transparent rounded-full mr-2"></div>
                      <span className="text-yellow-700 text-sm">Esperando confirmación...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
    );
};



// EmailConfirmedPage component moved outside App to prevent re-creation issues
const EmailConfirmedPage = ({ setCurrentView }) => {
  // Get URL parameters for verification result
  const urlParams = new URLSearchParams(window.location.search);
  const status = urlParams.get('status'); // 'approved', 'rejected', or 'email_verified'
  const reason = decodeURIComponent(urlParams.get('reason') || '');
  const solution = decodeURIComponent(urlParams.get('solution') || '');
  const hasSolution = urlParams.get('has_solution') === 'true';
  
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';
  const isEmailVerified = status === 'email_verified';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-2xl mx-auto text-center bg-white rounded-2xl shadow-xl p-8">
        {isEmailVerified ? (
          // EMAIL VERIFICATION CONFIRMED
          <>
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-4">
                <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">✅ Correo electrónico confirmado</h1>
            <p className="text-xl text-green-600 font-semibold mb-6">Puedes seguir llenando tu formulario</p>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">🚀 ¿Qué viene ahora?</h2>
              <div className="space-y-3 text-left">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-blue-600">1</span>
                  </div>
                  <span className="text-gray-700">Regresa a tu formulario y completa los datos restantes</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-blue-600">2</span>
                  </div>
                  <span className="text-gray-700">Nuestra IA verificará tu caso automáticamente</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-blue-600">3</span>
                  </div>
                  <span className="text-gray-700">Conectaremos tu caso con los mejores abogados</span>
                </div>
              </div>
            </div>

            <div className="bg-indigo-50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-bold text-indigo-900 mb-3">🎯 Con AyudaLegal IA obtienes:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-indigo-800">
                <div className="flex items-center space-x-2">
                  <svg className="h-4 w-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Verificación inteligente por IA</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="h-4 w-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Abogados especialistas verificados</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="h-4 w-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Respuesta en 24 horas</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="h-4 w-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Primera consulta gratuita</span>
                </div>
              </div>
            </div>

            <button 
              onClick={() => window.close()}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Continuar con mi formulario
            </button>
            
            <p className="text-sm text-gray-500 mt-4">
              Puedes cerrar esta ventana y continuar donde te quedaste
            </p>
          </>
        ) : isApproved ? (
          // APPROVED CASE
          <>
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">¡Tu caso ya fue publicado en AyudaLegal!</h1>
            <p className="text-lg text-gray-600 mb-6">
              Tu caso ha sido verificado exitosamente y enviado a abogados especializados. Recibirás propuestas profesionales pronto.
            </p>
            
            <button 
              onClick={() => setCurrentView('home')}
              className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700"
            >
              Ir a Pantalla Inicial
            </button>
          </>
        ) : isRejected ? (
          // REJECTED CASE
          <>
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-red-700 mb-4">
              Nuestra IA rechazó tu caso por: {reason}
            </h1>
            
            {hasSolution && solution ? (
              <>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700 font-medium">
                        💡 Solución: {solution}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => setCurrentView('case-form')}
                    className="bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700"
                  >
                    Intentar de Nuevo
                  </button>
                  <button 
                    onClick={() => setCurrentView('home')}
                    className="bg-gray-300 text-gray-700 px-6 py-3 rounded-full font-semibold hover:bg-gray-400"
                  >
                    Ir al Inicio
                  </button>
                </div>
              </>
            ) : (
              // NO SOLUTION (e.g., duplicates)
              <>
                <p className="text-lg text-gray-600 mb-6">
                  Tu caso no puede ser procesado en este momento.
                </p>
                
                <button 
                  onClick={() => setCurrentView('home')}
                  className="bg-gray-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-gray-700"
                >
                  Ir a Pantalla Inicial
                </button>
              </>
            )}
          </>
        ) : (
          // DEFAULT FALLBACK (should not happen)
          <>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">¡Tu caso ya fue publicado en AyudaLegal!</h1>
            <p className="text-lg text-gray-600 mb-6">
              Tu caso ha sido verificado exitosamente y enviado a abogados especializados. Recibirás propuestas profesionales pronto.
            </p>
            
            <button 
              onClick={() => setCurrentView('home')}
              className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700"
            >
              Ir a Pantalla Inicial
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Admin Login component
const AdminLogin = ({ setCurrentView, setAdminAuthenticated }) => {
  const [pin, setPin] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');

    // Validate admin PIN
    if (pin === '3917') {
      setAdminAuthenticated(true);
      setCurrentView('admin-panel');
    } else {
      setLoginError('PIN incorrecto');
    }
    
    setIsLoading(false);
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Solo números
    if (value.length <= 4) {
      setPin(value);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-blue-100 flex items-center justify-center p-4">
      <div className="max-w-sm w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Panel de Administración</h1>
            <p className="text-gray-600 mt-2">Ingresa tu PIN de acceso</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleAdminLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                PIN de Acceso
              </label>
              <input
                type="text"
                required
                value={pin}
                onChange={handlePinChange}
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-4 text-center text-2xl font-bold tracking-widest focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="••••"
                maxLength="4"
                style={{ letterSpacing: '0.5em' }}
              />
              <p className="text-xs text-gray-500 text-center mt-2">Ingresa tu PIN de 4 dígitos</p>
            </div>

            {loginError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm text-center">{loginError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || pin.length !== 4}
              className="w-full bg-gray-600 text-white py-4 px-4 rounded-lg font-semibold hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Verificando...' : 'Acceder'}
            </button>
          </form>

          {/* Back button */}
          <div className="mt-6 text-center">
            <button 
              onClick={() => setCurrentView('home')}
              className="text-gray-600 hover:text-gray-800 transition-colors text-sm"
            >
              ← Volver al inicio
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Partner Login component
const PartnerLogin = ({ backendUrl, setCurrentView, setPartnerAuthenticated }) => {
  const [pin, setPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handlePartnerLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');

    try {
      const response = await fetch(`${backendUrl}/api/partner/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pin })
      });

      const data = await response.json();
      
      if (data.success) {
        setPartnerAuthenticated(true);
        setCurrentView('partner-dashboard');
      } else {
        setLoginError(data.message);
      }
    } catch (error) {
      setLoginError('Error de conexión. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Solo números
    if (value.length <= 4) {
      setPin(value);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center px-4">
      <div className="max-w-sm w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-2xl">🔐</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard Socio</h1>
            <p className="text-gray-600 mt-2">Ingresa tu PIN de acceso</p>
          </div>

          <form onSubmit={handlePartnerLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                PIN de Acceso
              </label>
              <input
                type="text"
                required
                value={pin}
                onChange={handlePinChange}
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-4 text-center text-2xl font-bold tracking-widest focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="••••"
                maxLength="4"
                style={{ letterSpacing: '0.5em' }}
              />
              <p className="text-xs text-gray-500 text-center mt-2">Ingresa tu PIN de 4 dígitos</p>
            </div>

            {loginError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm text-center">{loginError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || pin.length !== 4}
              className="w-full bg-green-600 text-white py-4 px-4 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Verificando...' : 'Acceder'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button 
              onClick={() => setCurrentView('home')}
              className="text-gray-600 hover:text-gray-800 transition-colors text-sm"
            >
              ← Volver al inicio
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 📊 CASE TRACKING PAGE - Permite al cliente ver el estado de su caso
const CaseTrackingPage = ({ backendUrl, setCurrentView }) => {
  const [trackingData, setTrackingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const caseId = urlParams.get('case_id');
    const token = urlParams.get('token');
    
    if (!caseId || !token) {
      setError('Link de tracking inválido. Verifica el enlace que recibiste por email.');
      setIsLoading(false);
      return;
    }
    
    // Fetch tracking data
    fetch(`${backendUrl}/api/track/${caseId}/${token}`)
      .then(response => {
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Caso no encontrado');
          } else if (response.status === 403) {
            throw new Error('Link de tracking inválido o expirado');
          }
          throw new Error('Error al cargar el estado del caso');
        }
        return response.json();
      })
      .then(result => {
        if (result.success) {
          setTrackingData(result.data);
        } else {
          throw new Error(result.message || 'Error desconocido');
        }
      })
      .catch(err => {
        console.error('Error fetching tracking:', err);
        setError(err.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [backendUrl]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando estado de tu caso...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => setCurrentView('home')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }
  
  if (!trackingData) {
    return null;
  }
  
  const { status, status_display, is_taken, lawyer, stats, case_summary, recommendations, can_improve, resubmit_link } = trackingData;
  
  // Determinar color de fondo según estado
  const bgGradient = is_taken 
    ? 'from-green-50 to-emerald-100' 
    : status === 'expired' 
      ? 'from-orange-50 to-red-100'
      : 'from-blue-50 to-indigo-100';
  
  return (
    <div className={`min-h-screen bg-gradient-to-br ${bgGradient} py-8 px-4`}>
      <div className="max-w-2xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            📊 Estado de tu Caso
          </h1>
          <p className="text-gray-600">
            Área: <span className="font-semibold">{case_summary.area_legal}</span> • {case_summary.provincia}
          </p>
        </div>
        
        {/* Status Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="text-center mb-6">
            <div className="text-4xl mb-3">
              {is_taken ? '🎉' : status === 'expired' ? '⏰' : status === 'active' ? '🔍' : '⏳'}
            </div>
            <h2 className="text-xl font-bold text-gray-900">{status_display}</h2>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.lawyers_notified}</div>
              <div className="text-sm text-blue-800">Abogados notificados</div>
            </div>
            <div className={`${stats.days_remaining <= 2 ? 'bg-red-50' : 'bg-green-50'} rounded-xl p-4 text-center`}>
              <div className={`text-2xl font-bold ${stats.days_remaining <= 2 ? 'text-red-600' : 'text-green-600'}`}>
                {status === 'expired' ? '0' : stats.days_remaining}
              </div>
              <div className={`text-sm ${stats.days_remaining <= 2 ? 'text-red-800' : 'text-green-800'}`}>
                Días restantes
              </div>
            </div>
          </div>
          
          {/* Progress indicator */}
          {!is_taken && status === 'active' && (
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progreso del caso</span>
                <span>{7 - stats.days_remaining}/7 días</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all ${stats.days_remaining <= 2 ? 'bg-red-500' : 'bg-blue-500'}`}
                  style={{ width: `${((7 - stats.days_remaining) / 7) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
          
          {/* Case Summary */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-2">📝 Resumen de tu caso</h3>
            <p className="text-gray-600 text-sm">{case_summary.description_preview}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                {case_summary.area_legal}
              </span>
              <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">
                {case_summary.comuna}, {case_summary.provincia}
              </span>
              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs">
                Urgencia: {case_summary.urgency}
              </span>
            </div>
          </div>
        </div>
        
        {/* Lawyer Info - Si el caso fue tomado */}
        {is_taken && lawyer && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border-2 border-green-300">
            <div className="flex items-center mb-4">
              <div className="bg-green-100 rounded-full p-3 mr-4">
                <span className="text-2xl">👨‍⚖️</span>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">¡Un abogado tomó tu caso!</h3>
                <p className="text-green-600 text-sm">Te contactará pronto</p>
              </div>
            </div>
            
            <div className="bg-green-50 rounded-xl p-4 space-y-3">
              <div>
                <span className="text-sm text-gray-500">Nombre:</span>
                <p className="font-semibold text-gray-900">{lawyer.name}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Especialidades:</span>
                <p className="font-semibold text-gray-900">{lawyer.areas_legales?.join(', ')}</p>
              </div>
              {lawyer.university && (
                <div>
                  <span className="text-sm text-gray-500">Universidad:</span>
                  <p className="font-semibold text-gray-900">{lawyer.university}</p>
                </div>
              )}
              <div className="pt-3 border-t border-green-200">
                <p className="text-sm text-green-800">
                  📞 El abogado se comunicará contigo al teléfono y email que proporcionaste.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Recommendations - Si el caso no ha sido tomado */}
        {!is_taken && recommendations && recommendations.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border-2 border-amber-300">
            <div className="flex items-center mb-4">
              <div className="bg-amber-100 rounded-full p-3 mr-4">
                <span className="text-2xl">💡</span>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Recomendaciones para mejorar</h3>
                <p className="text-amber-600 text-sm">Mejora tu caso para atraer más abogados</p>
              </div>
            </div>
            
            <div className="space-y-3">
              {recommendations.map((rec, index) => (
                <div key={index} className="bg-amber-50 rounded-xl p-4 border-l-4 border-amber-400">
                  <div className="flex items-start">
                    <span className="text-xl mr-3">{rec.icon}</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">{rec.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{rec.message}</p>
                      <p className="text-sm text-amber-700 font-medium mt-2">
                        👉 {rec.action}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Success message if no recommendations and case is active */}
        {!is_taken && status === 'active' && (!recommendations || recommendations.length === 0) && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border-2 border-green-300">
            <div className="text-center">
              <div className="text-4xl mb-3">✅</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">Tu caso está bien descrito</h3>
              <p className="text-gray-600">
                Los abogados están revisando tu caso. Te notificaremos cuando uno lo tome.
              </p>
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="space-y-3">
          {can_improve && (
            <a
              href={status === 'expired' ? resubmit_link : `/?view=case-form&resubmit=true&case_id=${trackingData.case_id}`}
              className="block w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-center py-4 rounded-xl font-bold hover:from-blue-700 hover:to-indigo-700 transition shadow-lg"
            >
              🔄 {status === 'expired' ? 'Republicar caso mejorado' : 'Mejorar mi descripción'}
            </a>
          )}
          
          <button
            onClick={() => setCurrentView('home')}
            className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition"
          >
            ← Volver al inicio
          </button>
        </div>
        
        {/* Footer info */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>¿Tienes dudas? Contacta a soporte@ayudalegal.com.mx</p>
          <p className="mt-1">ID del caso: {trackingData.case_id.slice(0, 8)}...</p>
        </div>
      </div>
    </div>
  );
};

// Partner Dashboard component
const PartnerDashboard = ({ backendUrl, setCurrentView, setPartnerAuthenticated }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => {
    fetchStats();
    
    // Auto-refresh cada 3 horas (3 * 60 * 60 * 1000 = 10800000 ms)
    const interval = setInterval(() => {
      fetchStats(true); // Auto-refresh silencioso
    }, 10800000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async (isAutoRefresh = false) => {
    try {
      if (!isAutoRefresh) {
        setIsRefreshing(true);
      }
      
      const response = await fetch(`${backendUrl}/api/partner/stats`);
      const data = await response.json();
      setStats(data);
      setLastUpdated(new Date().toLocaleString('es-MX'));
    } catch (error) {
      console.error('Error fetching partner stats:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchStats();
  };

  const handleLogout = () => {
    setPartnerAuthenticated(false);
    setCurrentView('home');
  };

  const handleResetData = async () => {
    setIsResetting(true);
    setResetResult(null);
    try {
      const response = await fetch(`${backendUrl}/api/admin/reset-partner-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      
      if (response.ok) {
        setResetResult({
          success: true,
          message: `✅ Datos reseteados: ${data.results.lawyer_payments_deleted} pagos, ${data.results.package_orders_deleted} órdenes, ${data.results.lawyers_reset} abogados`
        });
        // Refrescar stats después del reset
        fetchStats();
      } else {
        setResetResult({ success: false, message: `❌ Error: ${data.detail}` });
      }
    } catch (error) {
      setResetResult({ success: false, message: `❌ Error de conexión: ${error.message}` });
    } finally {
      setIsResetting(false);
      setShowResetConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <span className="text-2xl mr-3">📊</span>
              <h1 className="text-xl font-bold text-gray-900">Dashboard Socio</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">
                Actualizado: {lastUpdated}
              </span>
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  isRefreshing 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                <span className={isRefreshing ? 'animate-spin' : ''}>🔄</span>
                <span>{isRefreshing ? 'Actualizando...' : 'Actualizar'}</span>
              </button>
              <button
                onClick={handleLogout}
                className="text-red-600 hover:text-red-800 text-sm font-medium"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Resumen Financiero */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Ayer */}
          <div className="bg-white rounded-xl shadow-sm p-6 border">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                <span className="text-purple-600 font-bold">📅</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Ayer</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Ingresos Brutos:</span>
                <span className="font-semibold">${stats?.yesterday_stats?.gross_income?.toLocaleString('en-US', {minimumFractionDigits: 2})} USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Gastos Publicidad (30%):</span>
                <span className="text-red-600">-${stats?.yesterday_stats?.advertising_costs?.toLocaleString('en-US', {minimumFractionDigits: 2})} USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Neto (post-gastos):</span>
                <span className="font-semibold">${stats?.yesterday_stats?.net_income?.toLocaleString('en-US', {minimumFractionDigits: 2})} USD</span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between">
                  <span className="text-purple-700 font-semibold">Participación (30%):</span>
                  <span className="text-purple-700 font-bold text-lg">${stats?.yesterday_stats?.partner_share?.toLocaleString('en-US', {minimumFractionDigits: 2})} USD</span>
                </div>
              </div>
            </div>
          </div>

          {/* Esta Semana */}
          <div className="bg-white rounded-xl shadow-sm p-6 border">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                <span className="text-green-600 font-bold">7D</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Esta Semana</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Ingresos Brutos:</span>
                <span className="font-semibold">${stats?.weekly_stats?.gross_income?.toLocaleString('en-US', {minimumFractionDigits: 2})} USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Gastos Publicidad (30%):</span>
                <span className="text-red-600">-${stats?.weekly_stats?.advertising_costs?.toLocaleString('en-US', {minimumFractionDigits: 2})} USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Neto (post-gastos):</span>
                <span className="font-semibold">${stats?.weekly_stats?.net_income?.toLocaleString('en-US', {minimumFractionDigits: 2})} USD</span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between">
                  <span className="text-green-700 font-semibold">Participación (30%):</span>
                  <span className="text-green-700 font-bold text-lg">${stats?.weekly_stats?.partner_share?.toLocaleString('en-US', {minimumFractionDigits: 2})} USD</span>
                </div>
              </div>
            </div>
          </div>

          {/* Acumulado Total */}
          <div className="bg-white rounded-xl shadow-sm p-6 border">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <span className="text-blue-600 font-bold">∑</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Acumulado Total</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Ingresos Brutos:</span>
                <span className="font-semibold">${stats?.total_stats?.gross_income?.toLocaleString('en-US', {minimumFractionDigits: 2})} USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Gastos Publicidad (30%):</span>
                <span className="text-red-600">-${stats?.total_stats?.advertising_costs?.toLocaleString('en-US', {minimumFractionDigits: 2})} USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Neto (post-gastos):</span>
                <span className="font-semibold">${stats?.total_stats?.net_income?.toLocaleString('en-US', {minimumFractionDigits: 2})} USD</span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between">
                  <span className="text-blue-700 font-semibold">Participación Total (30%):</span>
                  <span className="text-blue-700 font-bold text-xl">${stats?.total_stats?.partner_share?.toLocaleString('en-US', {minimumFractionDigits: 2})} USD</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Abogados por Estado */}
        <div className="bg-white rounded-xl shadow-sm p-6 border">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Abogados Registrados por Estado</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {stats?.lawyers_by_state && Object.entries(stats.lawyers_by_state)
              .sort(([,a], [,b]) => b - a) // Ordenar por cantidad descendente
              .map(([state, count]) => (
                <div key={state} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">{state}:</span>
                  <span className="font-semibold text-green-600">{count}</span>
                </div>
              ))
            }
          </div>
        </div>

        {/* Nota informativa */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <span className="text-blue-500 mr-3 mt-0.5">ℹ️</span>
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Información importante:</p>
              <ul className="space-y-1">
                <li>• Los datos se actualizan automáticamente cada 3 horas</li>
                <li>• Puedes actualizar manualmente usando el botón "Actualizar" en cualquier momento</li>
                <li>• Los pagos semanales se calculan de lunes a domingo</li>
                <li>• La participación es del 30% después de descontar gastos de publicidad (30%)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App component - moved after all other components to prevent re-creation issues
function App() {
  // Initialize state - check URL params on load and maintain routing
  const getInitialView = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');
    const tokenParam = urlParams.get('token');
    const payerIdParam = urlParams.get('PayerID');
    
    // IMPORTANT: Distinguish between PayPal token and reset-password token
    // PayPal returns: ?token=ORDER_ID&PayerID=PAYER_ID
    // Reset password uses: ?token=RESET_TOKEN (without PayerID)
    if (tokenParam && !payerIdParam && !viewParam) {
      // Only go to reset-password if token exists WITHOUT PayerID
      // This prevents PayPal callback from being misinterpreted
      return 'reset-password';
    }
    
    // Check for existing authentication
    const lawyerToken = localStorage.getItem('lawyer_token');
    const lawyerData = localStorage.getItem('lawyer_data');
    
    // If authenticated and no specific view requested, go to dashboard
    if (lawyerToken && lawyerData && !viewParam) {
      return 'lawyer-dashboard';
    }
    
    // Valid views that can be persisted in URL
    const validViews = [
      'home', 
      'case-form', 
      'lawyer-login', 
      'lawyer-register', 
      'lawyer-dashboard',
      'admin-login',
      'admin-panel',
      'partner-login',
      'partner-dashboard', 
      'email-confirmed',
      'track-case',
      'forgot-password',
      'reset-password'
    ];
    
    // If requesting dashboard but not authenticated, redirect to login
    if (viewParam === 'lawyer-dashboard' && (!lawyerToken || !lawyerData)) {
      return 'lawyer-login';
    }
    
    // If requesting partner dashboard but not authenticated, redirect to partner login
    if (viewParam === 'partner-dashboard' && localStorage.getItem('partner_authenticated') !== 'true') {
      return 'partner-login';
    }
    
    if (viewParam && validViews.includes(viewParam)) {
      return viewParam;
    }
    
    return 'home';
  };
  
  // Update URL when view changes (without page reload)
  const setCurrentViewWithURL = (view) => {
    setCurrentView(view);
    
    // Update URL parameter without page reload
    const url = new URL(window.location);
    if (view === 'home') {
      url.searchParams.delete('view');
    } else {
      url.searchParams.set('view', view);
    }
    
    // Use replaceState to avoid adding history entries for every view change
    window.history.replaceState(null, '', url.toString());
  };
  
  const [currentView, setCurrentView] = useState(getInitialView());
  const [caseId, setCaseId] = useState(null);
  const [showEmailConfirmedMessage, setShowEmailConfirmedMessage] = useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [caseStatus, setCaseStatus] = useState('pending_verification');
  const [adminAuthenticated, setAdminAuthenticated] = useState(false);
  const [partnerAuthenticated, setPartnerAuthenticated] = useState(() => {
    // Verificar si hay una sesión de socio guardada
    return localStorage.getItem('partner_authenticated') === 'true';
  });
  
  // Wrapper para setPartnerAuthenticated que siempre actualiza localStorage
  const setPartnerAuthenticatedWithPersistence = (value) => {
    if (value) {
      localStorage.setItem('partner_authenticated', 'true');
    } else {
      localStorage.removeItem('partner_authenticated');
    }
    setPartnerAuthenticated(value);
  };
  
  // Password recovery states
  const [forgotPasswordData, setForgotPasswordData] = useState({ email: '', message: '', isLoading: false });
  const [resetPasswordData, setResetPasswordData] = useState({ token: '', password: '', confirmPassword: '', message: '', isLoading: false });
  
  // NEW: Get URL parameters for case verification status
  const getVerificationParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      status: urlParams.get('status'), // 'approved' or 'rejected'
      reason: urlParams.get('reason'), // rejection reason
      solution: urlParams.get('solution'), // solution text
      hasSolution: urlParams.get('has_solution') === 'true'
    };
  };

  // Funciones para el nuevo flujo de verificación de email
  const startPolling = (caseId) => {
    // Polling cada 3 segundos para verificar confirmación de email
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${backendUrl}/api/cases/${caseId}/status`);
        if (response.ok) {
          const statusData = await response.json();
          setCaseStatus(statusData.status);
          
          if (statusData.status === 'active') {
            // Email confirmado y caso activo
            setShowEmailVerificationModal(false);
            setShowEmailConfirmedMessage(true);
            // Redirigir con parámetro de caso aprobado
            window.location.href = '/?view=email-confirmed&status=approved';
            clearInterval(interval);
            setPollingInterval(null);
          } else if (statusData.status === 'rejected') {
            // Caso rechazado
            setShowEmailVerificationModal(false);
            alert(`Caso rechazado: ${statusData.rejection_reason}`);
            clearInterval(interval);
            setPollingInterval(null);
          }
        }
      } catch (error) {
        console.error('Error verificando estado del caso:', error);
      }
    }, 3000);
    
    setPollingInterval(interval);
    
    // Limpiar después de 5 minutos si no hay respuesta
    setTimeout(() => {
      if (interval) {
        clearInterval(interval);
        setPollingInterval(null);
        setShowEmailVerificationModal(false);
        alert('El tiempo de verificación ha expirado. Por favor, intenta crear el caso nuevamente.');
      }
    }, 300000); // 5 minutos
  };

  const closeModal = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setShowEmailVerificationModal(false);
  };
  // Detectar automáticamente la URL del backend
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Initialize health check
  const healthCheck = HealthCheck({ backendUrl });

  // Initialize performance monitoring and cache on mount
  useEffect(() => {
    // Fix timing bug - handle cases where loadEventEnd might be 0
    if (typeof window !== 'undefined' && window.performance) {
      const timing = window.performance.timing;
      if (timing.loadEventEnd && timing.navigationStart) {
        const loadTime = timing.loadEventEnd - timing.navigationStart;
        console.log(`Page load time: ${loadTime}ms`);
      }
    }
    prefetchCriticalData(backendUrl);
  }, [backendUrl]);

  // Initialize authentication state and handle browser navigation
  useEffect(() => {
    // Check for existing authentication on app load
    const checkAuthenticationState = () => {
      const token = localStorage.getItem('lawyer_token');
      const lawyerData = localStorage.getItem('lawyer_data');
      
      // If we have a valid token and we're on lawyer-dashboard, keep that state
      if (token && lawyerData && currentView === 'lawyer-dashboard') {
        console.log('Authentication verified - maintaining dashboard session');
        return; // Keep current dashboard state
      }
      
      // If we have a token but not on dashboard, redirect to dashboard
      // 🆕 EXCLUIR vistas críticas que NO deben ser redirigidas
      const criticalViews = ['email-confirmed', 'case-form'];
      
      if (token && lawyerData && currentView !== 'lawyer-dashboard' && !criticalViews.includes(currentView)) {
        console.log('Authentication found - redirecting to dashboard');
        setCurrentView('lawyer-dashboard');
        return;
      }
    };

    const handlePopState = (event) => {
      // Prevent automatic navigation changes when user is authenticated
      const token = localStorage.getItem('lawyer_token');
      
      if (token && currentView === 'lawyer-dashboard') {
        // If user is authenticated and in dashboard, don't allow random navigation changes
        console.log('Preventing navigation change - user is authenticated');
        return;
      }
      
      // Only handle navigation for non-authenticated states
      const urlParams = new URLSearchParams(window.location.search);
      const viewParam = urlParams.get('view');
      
      const validViews = [
        'home', 
        'case-form', 
        'lawyer-login', 
        'lawyer-register', 
        'admin-login',
        'admin-panel',
        'partner-login',
        'partner-dashboard', 
        'email-confirmed',

        'forgot-password',
        'reset-password'
      ];
      
      console.log('🔍 DEBUG ROUTING:', { viewParam, validViews, includes: validViews.includes(viewParam) });
      
      if (viewParam && validViews.includes(viewParam)) {
        console.log('✅ Setting currentView to:', viewParam);
        setCurrentView(viewParam);
      } else if (!token) {
        console.log('✅ Setting currentView to: home (no token)');
        setCurrentView('home');
      }
    };

    // Check authentication on mount
    checkAuthenticationState();

    // Listen for browser back/forward button clicks
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentView]); // Include currentView as dependency

  // Main render logic
  return (
    <div className="App">
      {/* PostHog Analytics - managed safely in React lifecycle */}
      <PostHogAnalytics />
      
      {currentView === 'home' && <HomePage setCurrentView={setCurrentViewWithURL} backendUrl={backendUrl} />}
      {currentView === 'case-form' && <CaseForm backendUrl={backendUrl} setCaseId={setCaseId} setShowEmailConfirmedMessage={setShowEmailConfirmedMessage} setCurrentView={setCurrentViewWithURL} setShowEmailVerificationModal={setShowEmailVerificationModal} startPolling={startPolling} />}
      {currentView === 'lawyer-login' && <LawyerAuth backendUrl={backendUrl} setCurrentView={setCurrentViewWithURL} />}
      {currentView === 'lawyer-register' && <LawyerAuth backendUrl={backendUrl} setCurrentView={setCurrentViewWithURL} initialMode="register" />}
      {currentView === 'lawyer-dashboard' && <SimpleLawyerDashboard backendUrl={backendUrl} setCurrentView={setCurrentViewWithURL} />}
      {currentView === 'admin-login' && <AdminLogin setCurrentView={setCurrentViewWithURL} setAdminAuthenticated={setAdminAuthenticated} />}
      {currentView === 'partner-login' && <PartnerLogin backendUrl={backendUrl} setCurrentView={setCurrentViewWithURL} setPartnerAuthenticated={setPartnerAuthenticatedWithPersistence} />}
      {currentView === 'partner-dashboard' && partnerAuthenticated && <PartnerDashboard backendUrl={backendUrl} setCurrentView={setCurrentViewWithURL} setPartnerAuthenticated={setPartnerAuthenticatedWithPersistence} />}
      {currentView === 'admin-panel' && <AdminPanel setCurrentView={setCurrentViewWithURL} backendUrl={backendUrl} />}
      {currentView === 'email-confirmed' && <EmailConfirmedPage setCurrentView={setCurrentViewWithURL} />}
      {currentView === 'track-case' && <CaseTrackingPage backendUrl={backendUrl} setCurrentView={setCurrentViewWithURL} />}

      {currentView === 'forgot-password' && <ForgotPassword backendUrl={backendUrl} setCurrentView={setCurrentViewWithURL} forgotPasswordData={forgotPasswordData} setForgotPasswordData={setForgotPasswordData} />}
      {currentView === 'reset-password' && <ResetPassword backendUrl={backendUrl} setCurrentView={setCurrentViewWithURL} resetPasswordData={resetPasswordData} setResetPasswordData={setResetPasswordData} />}
      
      {/* Modal de Verificación de Email */}
      <EmailVerificationModal 
        showEmailVerificationModal={showEmailVerificationModal}
        closeModal={closeModal}
        caseStatus={caseStatus}
      />
    </div>
  );
}

// Admin Panel component
const AdminPanel = ({ setCurrentView, backendUrl }) => {
  const [pendingLawyers, setPendingLawyers] = useState([]);
  const [rejectedLawyers, setRejectedLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [showDocuments, setShowDocuments] = useState(false);
  
  // NUEVA FUNCIONALIDAD - Estados para métricas de regiones
  const [activeTab, setActiveTab] = useState('lawyers');
  const [wipeStep, setWipeStep] = useState(0);
  const [wipeLoading, setWipeLoading] = useState(false);
  const [wipeResult, setWipeResult] = useState(null); 'regions', 'system', 'email-marketing'
  const [regionMetrics, setRegionMetrics] = useState(null);
  const [regionLoading, setRegionLoading] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState([]); // Regiones seleccionadas
  const [bulkProcessing, setBulkProcessing] = useState(false); // Procesando múltiples

  // 🆕 EMAIL MARKETING - Estados
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [emailAnalytics, setEmailAnalytics] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [showCreateEmail, setShowCreateEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({
    subject: '',
    content: '', 
    audience: 'clients',
    aiPrompt: '',
    useAI: false
  });

  // 🚀 SISTEMA AUTO-HEALING - Estados
  const [systemHealth, setSystemHealth] = useState(null);
  const [systemLoading, setSystemLoading] = useState(false);
  const [retryOperation, setRetryOperation] = useState(null);

  // Load pending lawyers on component mount
  useEffect(() => {
    loadPendingLawyers();
    loadRejectedLawyers();
  }, []);

  // 🆕 EMAIL MARKETING FUNCTIONS
  const loadEmailAnalytics = async () => {
    try {
      setEmailLoading(true);
      const response = await fetch(`${backendUrl}/api/admin/email-marketing/analytics`);
      if (response.ok) {
        const data = await response.json();
        setEmailAnalytics(data);
      }
    } catch (error) {
      console.error('Error loading email analytics:', error);
    } finally {
      setEmailLoading(false);
    }
  };

  const generateAICopy = async () => {
    try {
      if (!emailForm.aiPrompt) return;
      
      setEmailLoading(true);
      const response = await fetch(`${backendUrl}/api/admin/email-marketing/generate-ai-copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: emailForm.aiPrompt,
          audience: emailForm.audience,
          email_type: 'marketing',
          personalization: {}
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEmailForm(prev => ({
            ...prev,
            subject: data.subject,
            content: data.content
          }));
        }
      }
    } catch (error) {
      console.error('Error generating AI copy:', error);
    } finally {
      setEmailLoading(false);
    }
  };

  const sendManualEmail = async () => {
    try {
      if (!emailForm.subject || !emailForm.content) return;
      
      setEmailLoading(true);
      const response = await fetch(`${backendUrl}/api/admin/email-marketing/send-manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: emailForm.subject,
          content: emailForm.content,
          audience: emailForm.audience,
          send_immediately: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✅ Email enviado exitosamente a ${data.sent_count} destinatarios`);
        setShowCreateEmail(false);
        setEmailForm({ subject: '', content: '', audience: 'clients', aiPrompt: '', useAI: false });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('❌ Error enviando email');
    } finally {
      setEmailLoading(false);
    }
  };

  const runBehavioralTriggers = async () => {
    try {
      setEmailLoading(true);
      const response = await fetch(`${backendUrl}/api/admin/email-marketing/run-triggers`, {
        method: 'POST'
      });
      
      if (response.ok) {
        alert('🤖 Triggers automáticos ejecutados exitosamente');
        loadEmailAnalytics(); // Recargar analytics
      }
    } catch (error) {
      console.error('Error running triggers:', error);
      alert('❌ Error ejecutando triggers');
    } finally {
      setEmailLoading(false);
    }
  };

  // NUEVA FUNCIONALIDAD - Cargar métricas de regiones
  const loadRegionMetrics = async () => {
    try {
      setRegionLoading(true);
      const response = await fetch(`${backendUrl}/api/admin/region-metrics`);
      if (response.ok) {
        const data = await response.json();
        setRegionMetrics(data);
      } else {
        console.error('Error loading region metrics:', response.status);
      }
    } catch (error) {
      console.error('Error loading region metrics:', error);
    } finally {
      setRegionLoading(false);
    }
  };

  // NUEVA FUNCIONALIDAD - Activar/desactivar región (CORREGIDO DOM ISSUE)
  const toggleRegion = async (regionName, currentStatus) => {
    try {
      setProcessingId(regionName);
      
      const action = currentStatus ? 'deactivate' : 'activate';
      const response = await fetch(`${backendUrl}/api/admin/toggle-region`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          region: regionName,
          action: action
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Region toggle result:', result);
        
        // Actualización suave del estado local en lugar de recargar todo
        setRegionMetrics(prevMetrics => {
          if (!prevMetrics || !prevMetrics.regions) return prevMetrics;
          
          const updatedRegions = prevMetrics.regions.map(region => {
            if (region.region === regionName) {
              return { ...region, is_active: !currentStatus };
            }
            return region;
          });
          
          // Actualizar contadores de resumen
          const activeCount = updatedRegions.filter(r => r.is_active).length;
          const inactiveCount = updatedRegions.length - activeCount;
          
          return {
            ...prevMetrics,
            regions: updatedRegions,
            summary: {
              ...prevMetrics.summary,
              active_regions: activeCount,
              inactive_regions: inactiveCount
            }
          };
        });
        
        // NO recargar automáticamente - usar el estado local actualizado
        // Esto evita conflictos y hace la UI más rápida
        
      } else {
        console.error('Error toggling region:', response.status);
        alert('Error cambiando estado de la región');
      }
    } catch (error) {
      console.error('Error toggling region:', error);
      alert('Error de conexión');
    } finally {
      // Limpiar processing state después de un delay para UI suave
      setTimeout(() => {
        setProcessingId(null);
      }, 300);
    }
  };

  // Seleccionar/deseleccionar región
  const toggleSelectRegion = (regionName) => {
    setSelectedRegions(prev => 
      prev.includes(regionName) 
        ? prev.filter(r => r !== regionName)
        : [...prev, regionName]
    );
  };

  // Seleccionar todas
  const selectAllRegions = () => {
    if (regionMetrics?.regions) {
      setSelectedRegions(regionMetrics.regions.map(r => r.region));
    }
  };

  // Deseleccionar todas
  const deselectAllRegions = () => {
    setSelectedRegions([]);
  };

  // Activar/desactivar múltiples regiones
  const bulkToggleRegions = async (action) => {
    if (selectedRegions.length === 0) {
      alert('Selecciona al menos una región');
      return;
    }
    
    setBulkProcessing(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/bulk-toggle-regions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regions: selectedRegions, action })
      });
      
      if (response.ok) {
        // Actualizar estado local
        setRegionMetrics(prev => ({
          ...prev,
          regions: prev.regions.map(r => ({
            ...r,
            is_active: selectedRegions.includes(r.region) 
              ? (action === 'activate') 
              : r.is_active
          }))
        }));
        setSelectedRegions([]);
        alert(`${selectedRegions.length} regiones ${action === 'activate' ? 'activadas' : 'desactivadas'}`);
      }
    } catch (error) {
      console.error('Error en bulk toggle:', error);
      alert('Error procesando regiones');
    } finally {
      setBulkProcessing(false);
    }
  };

  // 🚀 SISTEMA AUTO-HEALING - Funciones
  const loadSystemHealth = async () => {
    try {
      setSystemLoading(true);
      const response = await fetch(`${backendUrl}/api/admin/system-health-check`);
      if (response.ok) {
        const data = await response.json();
        setSystemHealth(data);
      } else {
        console.error('Error loading system health:', response.status);
      }
    } catch (error) {
      console.error('Error loading system health:', error);
    } finally {
      setSystemLoading(false);
    }
  };

  const fixRejectedLawyer = async (lawyerIdentifier) => {
    const confirmMessage = `¿Re-aprobar abogado: ${lawyerIdentifier}?\n\nEsto:\n- Re-activará al abogado\n- Ejecutará matching automático con casos existentes\n- Enviará email de bienvenida`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setRetryOperation('fix-rejected');
      const response = await fetch(`${backendUrl}/api/admin/fix-rejected-lawyer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lawyer_id: lawyerIdentifier })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ Abogado re-aprobado exitosamente!\n\nNombre: ${result.lawyer_name}\nEmail: ${result.lawyer_email}\nCasos asignados: ${result.matching_result?.matched_cases || 0}`);
        
        // Recargar salud del sistema
        if (systemHealth) await loadSystemHealth();
      } else {
        const error = await response.json();
        alert(`❌ Error re-aprobando abogado: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error fixing rejected lawyer:', error);
      alert('Error de conexión');
    } finally {
      setRetryOperation(null);
    }
  };

  const reprocessMatches = async (lawyerIdentifier) => {
    const confirmMessage = `¿Re-procesar matches para: ${lawyerIdentifier}?\n\nEsto buscará casos activos compatibles y enviará notificaciones.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setRetryOperation('reprocess-matches');
      const response = await fetch(`${backendUrl}/api/admin/reprocess-lawyer-matches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lawyer_id: lawyerIdentifier })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ Re-procesamiento completado!\n\nAbogado: ${result.lawyer_name}\nCasos asignados: ${result.matching_result?.matched_cases || 0}`);
      } else {
        const error = await response.json();
        alert(`❌ Error re-procesando: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error reprocessing matches:', error);
      alert('Error de conexión');
    } finally {
      setRetryOperation(null);
    }
  };

  const retryFailedNotifications = async () => {
    const confirmMessage = `¿Reintentar notificaciones fallidas?\n\nEsto re-enviará emails para casos de las últimas 24 horas.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setRetryOperation('retry-notifications');
      const response = await fetch(`${backendUrl}/api/admin/retry-failed-notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ Reintento completado!\n\nCasos procesados: ${result.cases_processed}\nNotificaciones reenviadas: ${result.notifications_resent}`);
      } else {
        const error = await response.json();
        alert(`❌ Error reintentando: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error retrying notifications:', error);
      alert('Error de conexión');
    } finally {
      setRetryOperation(null);
    }
  };
  const loadPendingLawyers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/admin/pending-lawyers`);
      setPendingLawyers(response.data.lawyers || []);
    } catch (error) {
      console.error('Error loading pending lawyers:', error);
      alert('Error cargando abogados pendientes');
    } finally {
      setLoading(false);
    }
  };

  const loadRejectedLawyers = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/rejected-lawyers`);
      setRejectedLawyers(response.data.lawyers || []);
    } catch (error) {
      console.error('Error loading rejected lawyers:', error);
      // No mostrar alert para no interrumpir UX, solo log
    }
  };

  const handleApprove = async (lawyerId) => {
    if (!window.confirm('¿Está seguro de aprobar este abogado?')) return;
    
    try {
      setProcessingId(lawyerId);
      await axios.post(`${backendUrl}/api/admin/approve-lawyer`, { lawyer_id: lawyerId });
      alert('Abogado aprobado exitosamente. Se ha enviado email de confirmación.');
      loadPendingLawyers(); // Reload pending list
      loadRejectedLawyers(); // Reload rejected list
    } catch (error) {
      console.error('Error approving lawyer:', error);
      alert('Error aprobando abogado');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (lawyerId) => {
    const reason = window.prompt('Motivo del rechazo (será enviado por email):');
    if (!reason) return;
    
    try {
      setProcessingId(lawyerId);
      await axios.post(`${backendUrl}/api/admin/reject-lawyer`, { lawyer_id: lawyerId, reason });
      alert('Abogado rechazado. Se ha enviado email de notificación.');
      loadPendingLawyers(); // Reload pending list
      loadRejectedLawyers(); // Reload rejected list
    } catch (error) {
      console.error('Error rejecting lawyer:', error);
      alert('Error rechazando abogado');
    } finally {
      setProcessingId(null);
    }
  };

  const viewDocuments = (lawyer) => {
    setSelectedLawyer(lawyer);
    setShowDocuments(true);
  };

  const logout = () => {
    setCurrentView('home');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Panel de Administración</h1>
              <p className="text-gray-600">Gestión de abogados - AyudaLegal IA</p>
            </div>
            <button 
              onClick={logout}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
        
        {/* NUEVA FUNCIONALIDAD - Tabs de navegación */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('lawyers')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'lawyers'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                👨‍💼 Gestión de Abogados
              </button>
              <button
                onClick={() => {
                  setActiveTab('regions');
                  if (!regionMetrics) loadRegionMetrics();
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'regions'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🌍 Control de Regiones
              </button>
              <button
                onClick={() => {
                  setActiveTab('system');
                  if (!systemHealth) loadSystemHealth();
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'system'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🔧 Auto-Healing Sistema
              </button>
              
              {/* 🆕 PESTAÑA EMAIL MARKETING */}
              <button
                onClick={() => {
                  setActiveTab('email-marketing');
                  if (!emailAnalytics) loadEmailAnalytics();
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'email-marketing'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📧 Email Marketing IA
              </button>
              <button
                onClick={() => setActiveTab('database')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'database'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Base de Datos
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {/* TAB: Gestión de Abogados (CONTENIDO EXISTENTE SIN CAMBIOS) */}
        {activeTab === 'lawyers' && (
          <>
            {/* CONTENIDO ORIGINAL DE GESTIÓN DE ABOGADOS */}
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{pendingLawyers.length}</p>
                <p className="text-gray-600">Pendientes</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">-</p>
                <p className="text-gray-600">Aprobados</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-red-600">{rejectedLawyers.length}</p>
                <p className="text-gray-600">Rechazados</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Lawyers Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Abogados Pendientes de Aprobación</h2>
            <p className="text-gray-600">Revise la información y documentos antes de aprobar</p>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando abogados pendientes...</p>
            </div>
          ) : pendingLawyers.length === 0 ? (
            <div className="p-8 text-center">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-600">No hay abogados pendientes de aprobación</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Abogado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Especialidades</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ubicación</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registro</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documentos</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingLawyers.map((lawyer) => (
                    <tr key={lawyer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{lawyer.name}</div>
                          <div className="text-sm text-gray-500">{lawyer.email}</div>
                          <div className="text-sm text-gray-500">RUT: {lawyer.rut}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {lawyer.areas_legales && lawyer.areas_legales.map(area => (
                            <span key={area} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-1 mb-1">
                              {area}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>{lawyer.region}</div>
                        <div className="text-gray-500">{lawyer.comunas && lawyer.comunas.slice(0, 2).join(', ')}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lawyer.created_at && new Date(lawyer.created_at).toLocaleDateString('es-CL')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => viewDocuments(lawyer)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Ver Documentos
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleApprove(lawyer.id)}
                            disabled={processingId === lawyer.id}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                          >
                            {processingId === lawyer.id ? '...' : 'Aprobar'}
                          </button>
                          <button
                            onClick={() => handleReject(lawyer.id)}
                            disabled={processingId === lawyer.id}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 disabled:opacity-50"
                          >
                            {processingId === lawyer.id ? '...' : 'Rechazar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>
        )}

      {/* Documents Modal */}
      {showDocuments && selectedLawyer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Documentos - {selectedLawyer.name}</h3>
                <button 
                  onClick={() => setShowDocuments(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Cedula */}
              {selectedLawyer.cedula_photo && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Cédula de Identidad</h4>
                  {selectedLawyer.cedula_photo.includes('data:application/pdf') ? (
                    <embed 
                      src={selectedLawyer.cedula_photo} 
                      type="application/pdf"
                      width="100%" 
                      height="500px"
                      className="border rounded"
                    />
                  ) : (
                    <img 
                      src={selectedLawyer.cedula_photo} 
                      alt="Cédula"
                      className="max-w-full h-auto border rounded"
                    />
                  )}
                </div>
              )}
              
              {/* Title Certificate */}
              {selectedLawyer.title_certificate && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Título Profesional</h4>
                  {selectedLawyer.title_certificate.includes('data:application/pdf') ? (
                    <div className="space-y-2">
                      <embed 
                        src={selectedLawyer.title_certificate} 
                        type="application/pdf"
                        width="100%" 
                        height="500px"
                        className="border rounded"
                      />
                      <div className="text-center">
                        <a 
                          href={selectedLawyer.title_certificate}
                          download="titulo_profesional.pdf"
                          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 inline-block"
                        >
                          📄 Descargar PDF
                        </a>
                      </div>
                    </div>
                  ) : (
                    <img 
                      src={selectedLawyer.title_certificate} 
                      alt="Título"
                      className="max-w-full h-auto border rounded"
                    />
                  )}
                </div>
              )}
              
              {/* Professional Info */}
              <div className="bg-gray-50 p-4 rounded">
                <h4 className="font-medium text-gray-900 mb-2">Información Profesional</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Universidad:</span> {selectedLawyer.university}
                  </div>
                  <div>
                    <span className="font-medium">Año Graduación:</span> {selectedLawyer.graduation_year}
                  </div>
                  <div>
                    <span className="font-medium">RUN Inscripción:</span> {selectedLawyer.run_inscription_number}
                  </div>
                  <div>
                    <span className="font-medium">Teléfono:</span> {selectedLawyer.phone}
                  </div>
                </div>
                {selectedLawyer.description && (
                  <div className="mt-4">
                    <span className="font-medium">Descripción:</span>
                    <p className="mt-1 text-gray-700">{selectedLawyer.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Rejected Lawyers Table - NUEVA FUNCIONALIDAD */}
        {rejectedLawyers.length > 0 && (
          <div className="bg-white rounded-lg shadow mt-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 text-red-700">📛 Abogados Rechazados</h2>
              <p className="text-gray-600">Lista de abogados que han sido rechazados con sus motivos</p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      👤 Información
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      📚 Especialidades
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      📅 Fecha Rechazo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ❌ Motivo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      🔧 Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rejectedLawyers.map((lawyer) => (
                    <tr key={lawyer.id} className="hover:bg-red-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-red-200 flex items-center justify-center">
                              <span className="text-sm font-medium text-red-800">
                                {lawyer.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{lawyer.name}</div>
                            <div className="text-sm text-gray-500">{lawyer.email}</div>
                            {lawyer.rut && (
                              <div className="text-xs text-gray-400">RUT: {lawyer.rut}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(lawyer.areas_legales || []).slice(0, 3).map((area, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                            >
                              {area}
                            </span>
                          ))}
                          {(lawyer.areas_legales || []).length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{(lawyer.areas_legales || []).length - 3} más
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lawyer.rejected_at ? 
                          new Date(lawyer.rejected_at).toLocaleDateString('es-CL') : 
                          'N/A'
                        }
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-red-700 max-w-xs">
                          {lawyer.rejection_reason || 'No especificado'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            if (window.confirm(`¿Re-aprobar a ${lawyer.name}?\n\nEsto volverá a activar al abogado y ejecutará matching automático.`)) {
                              // Usar la función de fix-rejected-lawyer
                              fixRejectedLawyer(lawyer.email);
                            }
                          }}
                          className="text-green-600 hover:text-green-900 mr-3"
                          title="Re-aprobar abogado"
                        >
                          ✅ Re-aprobar
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`¿Re-procesar matches para ${lawyer.name}?\n\nEsto buscará casos compatibles para este abogado.`)) {
                              reprocessMatches(lawyer.email);
                            }
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="Re-procesar matches"
                        >
                          🎯 Matches
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* TAB: Control de Regiones (NUEVA FUNCIONALIDAD) */}
        {activeTab === 'regions' && (
          <>
            {regionLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Cargando métricas de regiones...</p>
              </div>
            ) : regionMetrics ? (
              <>
                {/* Resumen General */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                      <div className="bg-blue-100 p-3 rounded-full">
                        <span className="text-2xl">🗺️</span>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Total Regiones</p>
                        <p className="text-2xl font-bold text-gray-900">{regionMetrics.summary.total_regions}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                      <div className="bg-green-100 p-3 rounded-full">
                        <span className="text-2xl">✅</span>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Regiones Activas</p>
                        <p className="text-2xl font-bold text-green-600">{regionMetrics.summary.active_regions}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                      <div className="bg-yellow-100 p-3 rounded-full">
                        <span className="text-2xl">📋</span>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Casos Totales</p>
                        <p className="text-2xl font-bold text-yellow-600">{regionMetrics.summary.total_cases}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                      <div className="bg-purple-100 p-3 rounded-full">
                        <span className="text-2xl">👨‍💼</span>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Abogados Activos</p>
                        <p className="text-2xl font-bold text-purple-600">{regionMetrics.summary.total_lawyers}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lista de Regiones */}
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">📊 Control de Regiones - Métricas en Tiempo Real</h3>
                    <p className="mt-1 text-sm text-gray-600">Gestiona qué regiones están activas y monitorea las métricas de cada una</p>
                  </div>
                  
                  {/* Barra de acciones masivas */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4 flex flex-wrap items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">
                      {selectedRegions.length} seleccionadas
                    </span>
                    <button
                      onClick={selectAllRegions}
                      className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    >
                      Seleccionar todas
                    </button>
                    <button
                      onClick={deselectAllRegions}
                      className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Deseleccionar
                    </button>
                    <div className="border-l border-gray-300 h-6 mx-2"></div>
                    <button
                      onClick={() => bulkToggleRegions('activate')}
                      disabled={selectedRegions.length === 0 || bulkProcessing}
                      className="px-4 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {bulkProcessing ? '...' : '✅ Activar seleccionadas'}
                    </button>
                    <button
                      onClick={() => bulkToggleRegions('deactivate')}
                      disabled={selectedRegions.length === 0 || bulkProcessing}
                      className="px-4 py-1.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {bulkProcessing ? '...' : '⭕ Desactivar seleccionadas'}
                    </button>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <input
                              type="checkbox"
                              checked={regionMetrics?.regions && selectedRegions.length === regionMetrics.regions.length}
                              onChange={(e) => e.target.checked ? selectAllRegions() : deselectAllRegions()}
                              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Región
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Estado
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Casos
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Abogados
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ratio
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Recomendación
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Control
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {regionMetrics.regions.map((region) => (
                          <tr key={`region-${region.region.replace(/\s+/g, '-').toLowerCase()}`} className={`hover:bg-gray-50 ${selectedRegions.includes(region.region) ? 'bg-blue-50' : ''}`}>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedRegions.includes(region.region)}
                                onChange={() => toggleSelectRegion(region.region)}
                                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className="text-sm font-medium text-gray-900">{region.region}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                region.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {region.is_active ? '✅ ACTIVA' : '⭕ INACTIVA'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="font-medium">📋 {region.cases.active} activos</div>
                                <div className="text-gray-500">Total: {region.cases.total}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="font-medium">👨‍💼 {region.lawyers.active}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <div className={`font-medium ${
                                region.metrics.ratio > 25 ? 'text-red-600' :
                                region.metrics.ratio > 15 ? 'text-yellow-600' :
                                region.metrics.ratio > 5 ? 'text-green-600' :
                                'text-gray-600'
                              }`}>
                                {region.metrics.ratio}:1
                              </div>
                              <div className="text-xs text-gray-500">casos/abogado</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                                region.metrics.recommendation === 'RECLUTAR_ABOGADOS' ? 'bg-red-100 text-red-800' :
                                region.metrics.recommendation === 'MONITOREAR' ? 'bg-yellow-100 text-yellow-800' :
                                region.metrics.recommendation === 'GENERAR_DEMANDA' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {region.metrics.recommendation.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                key={`toggle-${region.region}-${region.is_active}`}
                                onClick={() => toggleRegion(region.region, region.is_active)}
                                disabled={processingId === region.region}
                                className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                  region.is_active
                                    ? 'bg-red-100 text-red-800 hover:bg-red-200 disabled:opacity-50'
                                    : 'bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-50'
                                } ${processingId === region.region ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                {processingId === region.region ? (
                                  <span className="flex items-center">
                                    <div className="animate-spin rounded-full h-3 w-3 border border-current border-t-transparent mr-1"></div>
                                    Procesando...
                                  </span>
                                ) : region.is_active ? (
                                  <span>⭕ DESACTIVAR</span>
                                ) : (
                                  <span>✅ ACTIVAR</span>
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">Error cargando métricas. Intenta recargar la página.</p>
              </div>
            )}
          </>
        )}
        {/* TAB: Sistema Auto-Healing (NUEVA FUNCIONALIDAD) */}
        {activeTab === 'system' && (
          <>
            {systemLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Verificando salud del sistema...</p>
              </div>
            ) : systemHealth ? (
              <>
                {/* Resumen de Salud del Sistema */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                      <div className={`p-3 rounded-full ${systemHealth.database_status === 'healthy' ? 'bg-green-100' : 'bg-red-100'}`}>
                        <span className="text-2xl">{systemHealth.database_status === 'healthy' ? '✅' : '❌'}</span>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Base de Datos</p>
                        <p className={`text-lg font-bold ${systemHealth.database_status === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>
                          {systemHealth.database_status === 'healthy' ? 'OPERATIVA' : 'ERROR'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                      <div className="bg-blue-100 p-3 rounded-full">
                        <span className="text-2xl">👨‍💼</span>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Abogados Verificados</p>
                        <p className="text-2xl font-bold text-blue-600">{systemHealth.lawyers_stats.verified}</p>
                        <p className="text-xs text-gray-500">de {systemHealth.lawyers_stats.total} total</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                      <div className="bg-orange-100 p-3 rounded-full">
                        <span className="text-2xl">📋</span>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Casos Activos</p>
                        <p className="text-2xl font-bold text-orange-600">{systemHealth.cases_stats.active}</p>
                        <p className="text-xs text-gray-500">24h: {systemHealth.cases_stats.recent_24h}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                      <div className={`p-3 rounded-full ${systemHealth.matching_health.notification_rate > 50 ? 'bg-green-100' : 'bg-yellow-100'}`}>
                        <span className="text-2xl">{systemHealth.matching_health.notification_rate > 50 ? '🎯' : '⚠️'}</span>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Tasa Notificación</p>
                        <p className={`text-2xl font-bold ${systemHealth.matching_health.notification_rate > 50 ? 'text-green-600' : 'text-yellow-600'}`}>
                          {systemHealth.matching_health.notification_rate}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Herramientas de Auto-Healing */}
                <div className="bg-white rounded-lg shadow mb-8">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">🔧 Herramientas de Auto-Healing</h2>
                    <p className="text-gray-600">Herramientas para solucionar problemas automáticamente</p>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Re-aprobar Abogado Rechazado */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-medium text-gray-900 mb-2">🔄 Re-aprobar Abogado</h3>
                        <p className="text-sm text-gray-600 mb-4">
                          Re-aprueba un abogado rechazado por error y ejecuta matching automático con casos existentes.
                        </p>
                        <div className="mb-3">
                          <input
                            type="text"
                            placeholder="ID, Email o RUT del abogado"
                            className="w-full p-2 border rounded text-sm"
                            id="fix-lawyer-input"
                          />
                        </div>
                        <button
                          onClick={() => {
                            const input = document.getElementById('fix-lawyer-input');
                            if (input.value.trim()) {
                              fixRejectedLawyer(input.value.trim());
                            } else {
                              alert('Ingresa ID, email o RUT del abogado');
                            }
                          }}
                          disabled={retryOperation === 'fix-rejected'}
                          className="w-full bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                        >
                          {retryOperation === 'fix-rejected' ? '⏳ Procesando...' : '✅ Re-aprobar'}
                        </button>
                      </div>

                      {/* Re-procesar Matches */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-medium text-gray-900 mb-2">🎯 Re-procesar Matches</h3>
                        <p className="text-sm text-gray-600 mb-4">
                          Re-ejecuta el matching para un abogado específico con todos los casos activos compatibles.
                        </p>
                        <div className="mb-3">
                          <input
                            type="text"
                            placeholder="ID o Email del abogado"
                            className="w-full p-2 border rounded text-sm"
                            id="reprocess-lawyer-input"
                          />
                        </div>
                        <button
                          onClick={() => {
                            const input = document.getElementById('reprocess-lawyer-input');
                            if (input.value.trim()) {
                              reprocessMatches(input.value.trim());
                            } else {
                              alert('Ingresa ID o email del abogado');
                            }
                          }}
                          disabled={retryOperation === 'reprocess-matches'}
                          className="w-full bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                          {retryOperation === 'reprocess-matches' ? '⏳ Procesando...' : '🎯 Re-procesar'}
                        </button>
                      </div>

                      {/* Reintentar Notificaciones */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-medium text-gray-900 mb-2">📧 Reintentar Notificaciones</h3>
                        <p className="text-sm text-gray-600 mb-4">
                          Re-envía emails de notificación para casos de las últimas 24 horas que pueden haber fallado.
                        </p>
                        <div className="mb-3 text-sm text-gray-500">
                          Casos sin notificar: {systemHealth.matching_health.cases_without_notifications}
                        </div>
                        <button
                          onClick={retryFailedNotifications}
                          disabled={retryOperation === 'retry-notifications'}
                          className="w-full bg-orange-600 text-white px-4 py-2 rounded text-sm hover:bg-orange-700 disabled:opacity-50"
                        >
                          {retryOperation === 'retry-notifications' ? '⏳ Enviando...' : '📧 Reintentar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Estadísticas Detalladas */}
                <div className="bg-white rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">📊 Estadísticas del Sistema</h2>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {/* Estadísticas de Abogados */}
                      <div>
                        <h3 className="font-medium text-gray-900 mb-4">👨‍💼 Estado de Abogados</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">✅ Verificados</span>
                            <span className="font-medium">{systemHealth.lawyers_stats.verified}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">⏳ Pendientes</span>
                            <span className="font-medium">{systemHealth.lawyers_stats.pending}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">❌ Rechazados</span>
                            <span className="font-medium">{systemHealth.lawyers_stats.rejected}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2">
                            <span className="text-sm font-medium text-gray-900">📊 Total</span>
                            <span className="font-bold">{systemHealth.lawyers_stats.total}</span>
                          </div>
                        </div>
                      </div>

                      {/* Estadísticas de Matching */}
                      <div>
                        <h3 className="font-medium text-gray-900 mb-4">🎯 Salud del Matching</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">✅ Casos con notificaciones</span>
                            <span className="font-medium">{systemHealth.matching_health.cases_with_notifications}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">❌ Casos sin notificar</span>
                            <span className="font-medium">{systemHealth.matching_health.cases_without_notifications}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">📈 Tasa de éxito</span>
                            <span className={`font-medium ${systemHealth.matching_health.notification_rate > 50 ? 'text-green-600' : 'text-yellow-600'}`}>
                              {systemHealth.matching_health.notification_rate}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Información del Sistema */}
                <div className="mt-6 text-center text-sm text-gray-500">
                  Última verificación: {new Date(systemHealth.timestamp).toLocaleString('es-CL')}
                  <button
                    onClick={loadSystemHealth}
                    className="ml-4 text-blue-600 hover:text-blue-800"
                  >
                    🔄 Actualizar
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">Error cargando salud del sistema. Intenta recargar la página.</p>
                <button
                  onClick={loadSystemHealth}
                  className="mt-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  🔄 Reintentar
                </button>
              </div>
            )}
          </>
        )}

        {/* 🆕 EMAIL MARKETING TAB */}
        {activeTab === 'email-marketing' && (
          <div className="space-y-8">
            {/* Header con Analytics */}
            <div className="bg-gradient-to-r from-purple-500 to-blue-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">📧 Email Marketing con IA</h2>
                  <p className="text-purple-100">Sistema automático + manual para reactivación de usuarios</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold">{emailAnalytics?.total_campaigns || 0}</div>
                  <div className="text-sm text-purple-200">Campañas Totales</div>
                </div>
              </div>
            </div>

            {/* Acciones Rápidas */}
            <div className="grid md:grid-cols-3 gap-6">
              <button
                onClick={() => setShowCreateEmail(true)}
                className="bg-green-500 hover:bg-green-600 text-white p-6 rounded-xl transition-colors"
              >
                <div className="text-3xl mb-2">✍️</div>
                <div className="font-bold text-lg">Crear Email Manual</div>
                <div className="text-sm text-green-100">Con asistente IA</div>
              </button>
              
              <button
                onClick={runBehavioralTriggers}
                disabled={emailLoading}
                className="bg-blue-500 hover:bg-blue-600 text-white p-6 rounded-xl transition-colors disabled:opacity-50"
              >
                <div className="text-3xl mb-2">🤖</div>
                <div className="font-bold text-lg">Ejecutar Triggers</div>
                <div className="text-sm text-blue-100">Automáticos</div>
              </button>
              
              <button
                onClick={loadEmailAnalytics}
                className="bg-purple-500 hover:bg-purple-600 text-white p-6 rounded-xl transition-colors"
              >
                <div className="text-3xl mb-2">📊</div>
                <div className="font-bold text-lg">Ver Analytics</div>
                <div className="text-sm text-purple-100">Estadísticas</div>
              </button>
            </div>

            {/* Modal Crear Email */}
            {showCreateEmail && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold">📧 Crear Email Marketing</h3>
                    <button
                      onClick={() => setShowCreateEmail(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Configuración */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Audiencia</label>
                        <select
                          value={emailForm.audience}
                          onChange={(e) => setEmailForm(prev => ({...prev, audience: e.target.value}))}
                          className="w-full border rounded-lg px-3 py-2"
                        >
                          <option value="clients">👥 Clientes (con casos activos)</option>
                          <option value="lawyers">⚖️ Abogados (verificados)</option>
                        </select>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={emailForm.useAI}
                            onChange={(e) => setEmailForm(prev => ({...prev, useAI: e.target.checked}))}
                            className="mr-2"
                          />
                          <label className="font-medium">🧠 Generar con IA</label>
                        </div>
                        
                        {emailForm.useAI && (
                          <div className="space-y-2">
                            <textarea
                              placeholder="Ej: Crear email para reactivar clientes que no han encontrado abogado en 7 días"
                              value={emailForm.aiPrompt}
                              onChange={(e) => setEmailForm(prev => ({...prev, aiPrompt: e.target.value}))}
                              className="w-full border rounded-lg px-3 py-2"
                              rows="3"
                            />
                            <button
                              onClick={generateAICopy}
                              disabled={emailLoading || !emailForm.aiPrompt}
                              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                            >
                              {emailLoading ? '🔄 Generando...' : '🧠 Generar Copy'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Asunto</label>
                        <input
                          type="text"
                          value={emailForm.subject}
                          onChange={(e) => setEmailForm(prev => ({...prev, subject: e.target.value}))}
                          className="w-full border rounded-lg px-3 py-2"
                          placeholder="Asunto del email..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Contenido</label>
                        <textarea
                          value={emailForm.content}
                          onChange={(e) => setEmailForm(prev => ({...prev, content: e.target.value}))}
                          className="w-full border rounded-lg px-3 py-2"
                          rows="10"
                          placeholder="Contenido del email (HTML permitido)..."
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4 mt-6">
                    <button
                      onClick={sendManualEmail}
                      disabled={emailLoading || !emailForm.subject || !emailForm.content}
                      className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg disabled:opacity-50 font-medium"
                    >
                      {emailLoading ? '📤 Enviando...' : '🚀 Enviar Email'}
                    </button>
                    <button
                      onClick={() => setShowCreateEmail(false)}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Display */}
            {emailAnalytics && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4">📊 Estadísticas del Sistema</h3>
                <div className="grid md:grid-cols-2 gap-4 text-center">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{emailAnalytics.total_campaigns}</div>
                    <div className="text-sm text-gray-600">Campañas Creadas</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {emailAnalytics.email_stats?.total_sent || 0}
                    </div>
                    <div className="text-sm text-gray-600">Emails Enviados</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB: Base de Datos */}
        {activeTab === 'database' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Base de Datos</h2>
              <p className="text-sm text-gray-500 mb-6">Herramientas de mantenimiento para producción</p>

              {/* Wipe All Data */}
              <div className="border-2 border-red-200 rounded-xl p-6 bg-red-50">
                <h3 className="text-lg font-bold text-red-800 mb-2">Vaciar Base de Datos Completa</h3>
                <p className="text-sm text-red-700 mb-4">
                  Elimina TODOS los datos: abogados, casos, pagos, notificaciones. 
                  La aplicación quedará completamente vacía. <strong>Esta acción es irreversible.</strong>
                </p>

                <div>
                  {wipeStep === 0 && (
                    <button
                      onClick={() => setWipeStep(1)}
                      className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
                      data-testid="wipe-data-btn"
                    >
                      Vaciar toda la base de datos
                    </button>
                  )}

                  {wipeStep === 1 && (
                    <div className="bg-white border-2 border-red-400 rounded-lg p-4 space-y-3">
                      <p className="text-sm font-bold text-red-800">PRIMERA CONFIRMACIÓN</p>
                      <p className="text-sm text-red-700">¿Estás seguro? Se borrarán TODOS los abogados, casos, pagos y notificaciones.</p>
                      <div className="flex gap-3">
                        <button onClick={() => setWipeStep(2)} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg text-sm" data-testid="wipe-confirm-1">
                          Sí, continuar
                        </button>
                        <button onClick={() => setWipeStep(0)} className="bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-2 px-4 rounded-lg text-sm">
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  {wipeStep === 2 && (
                    <div className="bg-white border-2 border-red-600 rounded-lg p-4 space-y-3">
                      <p className="text-sm font-bold text-red-900">ÚLTIMA CONFIRMACIÓN</p>
                      <p className="text-sm text-red-800 font-semibold">Esto es IRREVERSIBLE. Toda la información se perderá permanentemente.</p>
                      <div className="flex gap-3">
                        <button 
                          onClick={async () => {
                            setWipeLoading(true);
                            try {
                              const response = await fetch(`${backendUrl}/api/admin/wipe-all-data`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ pin: '3917', confirm: 'BORRAR TODO' })
                              });
                              const data = await response.json();
                              if (response.ok) {
                                setWipeResult({ success: true, data });
                                setWipeStep(3);
                              } else {
                                setWipeResult({ success: false, error: data.detail });
                              }
                            } catch (error) {
                              setWipeResult({ success: false, error: error.message });
                            } finally {
                              setWipeLoading(false);
                            }
                          }}
                          disabled={wipeLoading}
                          className="bg-red-700 hover:bg-red-800 text-white font-bold py-2 px-6 rounded-lg text-sm disabled:opacity-50"
                          data-testid="wipe-confirm-final"
                        >
                          {wipeLoading ? 'Borrando...' : 'BORRAR TODO DEFINITIVAMENTE'}
                        </button>
                        <button onClick={() => setWipeStep(0)} className="bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-2 px-4 rounded-lg text-sm">
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  {wipeStep === 3 && wipeResult?.success && (
                    <div className="bg-green-50 border-2 border-green-400 rounded-lg p-4">
                      <p className="text-sm font-bold text-green-800 mb-2">Base de datos vaciada exitosamente</p>
                      <p className="text-sm text-green-700">{wipeResult.data.total_deleted} documentos eliminados</p>
                      <div className="mt-2 text-xs text-green-600 space-y-0.5">
                        {Object.entries(wipeResult.data.collections_cleared).map(([col, count]) => (
                          <div key={col}>{col}: {count} eliminados</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {wipeResult && !wipeResult.success && (
                    <div className="bg-red-100 border border-red-400 rounded-lg p-3 mt-3">
                      <p className="text-sm text-red-800">Error: {wipeResult.error}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;